import * as THREE from "three";

/**
 * Procedural, tileable surface textures generated on a canvas (no asset files).
 * They're near-white so a MeshStandardMaterial's `color` tints them — adding
 * panel seams, rivets and grain to every surface so maps look built, not flat.
 * Cached module-wide and shared across all boxes (one GPU upload).
 *
 * 2026-07-31 (mission `graphics`): every family now also ships a real tangent-
 * space NORMAL map and a ROUGHNESS map, not just the old "reuse the albedo as a
 * bumpMap" trick. That trick was doing almost nothing visible — a near-white
 * albedo has a tiny luminance range, `bumpMap` differentiates it in the shader
 * at whatever mip the pixel lands on, and `building`/`ground` (the two families
 * that cover most of the screen) had no bump at all. A dedicated height field
 * per family, sobel'd into a normal map, gives brick/stone/concrete a real
 * directional light response; the roughness map breaks the uniform plastic
 * sheen that made every surface read as the same material.
 *
 * Budget: albedo 512², normal 256², roughness 128². One upload each, module
 * cached forever, generated once on the first map load — the build logs its own
 * wall time as `[maptex] generated N families in X ms` so the cost is never a
 * guess. Normal + roughness maps are SKIPPED on lowSpec() machines; they cost
 * fill rate, and a machine that already gave up shadows and post should not
 * pay for them.
 */

const ALBEDO = 512;
const NORMAL = 256;
const ROUGH = 128;

export type SurfaceFamily =
  | "surface" | "plaster" | "wood" | "concrete" | "facade" | "terrain" | "trim" | "paving"
  | "bark" | "canopy" | "needle" | "hillside" | "boulder" | "cloth" | "roofing" | "galv" | "louvre" | "signage"
  | "shopwin"
  | "minecraft" | "mcStone" | "mcBrick" | "mcWood" | "mcFoliage" | "mcEarth" | "mcGlass";

/** albedo + the optional dedicated height/roughness fields a maker can supply.
 *  When `height` is absent the albedo's luminance is used (works well for the
 *  patterns whose dark pixels ARE the recesses: mortar, cracks, wood grain). */
interface Layers {
  albedo: HTMLCanvasElement;
  height?: HTMLCanvasElement;
  /** white = rough, black = glossy. Absent → a flat mid-rough constant. */
  rough?: HTMLCanvasElement;
  /** normal-map strength; 0 disables the normal map for this family. */
  bump: number;
}

interface FamilyTex {
  map: THREE.CanvasTexture;
  normal: THREE.CanvasTexture | null;
  roughness: THREE.CanvasTexture | null;
  bump: number;
}

type MapTexCache = Record<SurfaceFamily, FamilyTex> & {
  facadeGlow: THREE.CanvasTexture;
  /** 2x2 ATLAS of lit sign faces — see makeSignage. Pairs with `signage`. */
  signageGlow: THREE.CanvasTexture;
  /** 2x2 ATLAS of lit shop interiors — see makeShopwin. Pairs with `shopwin`. */
  shopwinGlow: THREE.CanvasTexture;
};
let cache: MapTexCache | null = null;

function cvs(size: number): { c: HTMLCanvasElement; g: CanvasRenderingContext2D } {
  const c = document.createElement("canvas");
  c.width = c.height = size;
  return { c, g: c.getContext("2d", { willReadFrequently: true })! };
}

/** Per-pixel grain, blended straight into the backing ImageData.
 *
 *  This used to be `n` separate `fillStyle = rgba(...)` + `fillRect(x,y,1,1)`
 *  pairs. At 256² that was ~6k calls; the `graphics` pass moved to 512², which
 *  needs 4× the samples for the same visual density — and 350k canvas state
 *  changes measured **766 ms** of dead time on the first map load (logged by
 *  the `[maptex]` line below). One getImageData + a flat typed-array loop + one
 *  putImageData is the same picture for a fraction of the cost. */
function noise(g: CanvasRenderingContext2D, n: number, alpha: number, size = ALBEDO): void {
  const img = g.getImageData(0, 0, size, size);
  const d = img.data;
  for (let i = 0; i < n; i++) {
    const j = (((Math.random() * size) | 0) + ((Math.random() * size) | 0) * size) * 4;
    const v = 150 + Math.random() * 105;
    d[j] += (v - d[j]) * alpha;
    d[j + 1] += (v - d[j + 1]) * alpha;
    d[j + 2] += (v - d[j + 2]) * alpha;
  }
  g.putImageData(img, 0, 0);
}

/**
 * Sobel a grayscale height field into a tangent-space normal map, wrapping at
 * the edges so the result tiles exactly like its source. Green is +Y (three's
 * default tangent convention for a flipped-Y UV set is handled by
 * `normalScale.y`, which the callers leave positive — these are all synthetic
 * fields, so "which way is up" is a look choice, not a correctness one).
 */
export function normalMapFrom(src: HTMLCanvasElement, strength: number, size = NORMAL): HTMLCanvasElement {
  const NORMAL = size;
  const { c, g } = cvs(NORMAL);
  g.drawImage(src, 0, 0, NORMAL, NORMAL);
  const img = g.getImageData(0, 0, NORMAL, NORMAL);
  const d = img.data;
  // luminance pass first — sampling it twice per neighbour would be 4× the work
  const h = new Float32Array(NORMAL * NORMAL);
  for (let i = 0; i < h.length; i++) {
    h[i] = (d[i * 4] * 0.299 + d[i * 4 + 1] * 0.587 + d[i * 4 + 2] * 0.114) / 255;
  }
  const out = g.createImageData(NORMAL, NORMAL);
  const o = out.data;
  const at = (x: number, y: number): number => h[((y + NORMAL) % NORMAL) * NORMAL + ((x + NORMAL) % NORMAL)];
  for (let y = 0; y < NORMAL; y++) {
    for (let x = 0; x < NORMAL; x++) {
      const dx = (at(x + 1, y - 1) + 2 * at(x + 1, y) + at(x + 1, y + 1))
        - (at(x - 1, y - 1) + 2 * at(x - 1, y) + at(x - 1, y + 1));
      const dy = (at(x - 1, y + 1) + 2 * at(x, y + 1) + at(x + 1, y + 1))
        - (at(x - 1, y - 1) + 2 * at(x, y - 1) + at(x + 1, y - 1));
      let nx = -dx * strength, ny = -dy * strength, nz = 1;
      const len = Math.hypot(nx, ny, nz);
      nx /= len; ny /= len; nz /= len;
      const i = (y * NORMAL + x) * 4;
      o[i] = (nx * 0.5 + 0.5) * 255;
      o[i + 1] = (ny * 0.5 + 0.5) * 255;
      o[i + 2] = (nz * 0.5 + 0.5) * 255;
      o[i + 3] = 255;
    }
  }
  g.putImageData(out, 0, 0);
  return c;
}

/** Fallback roughness: darker albedo → rougher, remapped into [lo,hi]. */
export function roughFrom(src: HTMLCanvasElement, lo: number, hi: number, size = ROUGH): HTMLCanvasElement {
  const ROUGH = size;
  const { c, g } = cvs(ROUGH);
  g.drawImage(src, 0, 0, ROUGH, ROUGH);
  const img = g.getImageData(0, 0, ROUGH, ROUGH);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    const lum = (d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114) / 255;
    const r = Math.round((hi - (hi - lo) * lum) * 255);
    d[i] = d[i + 1] = d[i + 2] = r;
  }
  g.putImageData(img, 0, 0);
  return c;
}

/** Industrial panel: base + a grid of seams, rivets, subtle grain.
 *  The height field is drawn separately so seams cut IN and rivets stand OUT —
 *  deriving it from the albedo made the bright rivet highlight a crater. */
function makeSurface(): Layers {
  const { c, g } = cvs(ALBEDO);
  const { c: hc, g: hg } = cvs(ALBEDO);
  g.fillStyle = "#e6e6e6"; g.fillRect(0, 0, ALBEDO, ALBEDO);
  hg.fillStyle = "#808080"; hg.fillRect(0, 0, ALBEDO, ALBEDO);
  noise(g, 22000, 0.12);
  // brushed-metal streaks — a strong directional cue the old flat panel lacked
  for (let i = 0; i < 900; i++) {
    const y = Math.random() * ALBEDO, len = 30 + Math.random() * 200;
    g.strokeStyle = `rgba(${Math.random() < 0.5 ? "255,255,255,0.05" : "70,70,80,0.05"})`;
    g.lineWidth = 1;
    g.beginPath(); g.moveTo(Math.random() * ALBEDO, y); g.lineTo(Math.random() * ALBEDO + len, y); g.stroke();
  }
  for (let i = 0; i < 34; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 20 + Math.random() * 80;
    const grd = g.createRadialGradient(x, y, 0, x, y, r);
    grd.addColorStop(0, "rgba(110,105,95,0.07)");
    grd.addColorStop(1, "rgba(110,105,95,0)");
    g.fillStyle = grd; g.fillRect(x - r, y - r, r * 2, r * 2);
  }
  const seams = [0, 128, 256, 384];
  // panel seams (tileable) — dark line + bevel highlight, and a real groove
  for (const p of seams) {
    for (const [ctx, dark, light, w] of [[g, "rgba(0,0,0,0.20)", "rgba(255,255,255,0.24)", 4] as const,
      [hg, "rgba(0,0,0,0.85)", "rgba(255,255,255,0.55)", 5] as const]) {
      ctx.lineWidth = w; ctx.strokeStyle = dark;
      ctx.beginPath(); ctx.moveTo(p + 2, 0); ctx.lineTo(p + 2, ALBEDO); ctx.moveTo(0, p + 2); ctx.lineTo(ALBEDO, p + 2); ctx.stroke();
      ctx.lineWidth = 2; ctx.strokeStyle = light;
      ctx.beginPath(); ctx.moveTo(p + 7, 0); ctx.lineTo(p + 7, ALBEDO); ctx.moveTo(0, p + 7); ctx.lineTo(ALBEDO, p + 7); ctx.stroke();
    }
  }
  // rivets — raised domes, so they get a bright top in the height field
  for (const x of seams) for (const y of seams) {
    for (const [ox, oy] of [[16, 16], [16, 112], [112, 16], [112, 112]] as const) {
      const cx = x + ox, cy = y + oy;
      g.fillStyle = "rgba(0,0,0,0.20)"; g.beginPath(); g.arc(cx + 1, cy + 1, 5, 0, 7); g.fill();
      g.fillStyle = "rgba(255,255,255,0.38)"; g.beginPath(); g.arc(cx - 0.8, cy - 0.8, 3.4, 0, 7); g.fill();
      const dome = hg.createRadialGradient(cx, cy, 0, cx, cy, 5.5);
      dome.addColorStop(0, "#ffffff"); dome.addColorStop(1, "rgba(128,128,128,0)");
      hg.fillStyle = dome; hg.beginPath(); hg.arc(cx, cy, 5.5, 0, 7); hg.fill();
    }
  }
  return { albedo: c, height: hc, bump: 2.6 };
}

/** Rougher concrete/asphalt for large flat surfaces (ground). */
function makeConcrete(): Layers {
  const { c, g } = cvs(ALBEDO);
  g.fillStyle = "#e2e2e2"; g.fillRect(0, 0, ALBEDO, ALBEDO);
  noise(g, 56000, 0.14);
  // exposed aggregate: hundreds of small stones with a lit top and dark base —
  // the single detail that stops a big slab reading as painted cardboard
  for (let i = 0; i < 1400; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 1.2 + Math.random() * 4.5;
    const v = 90 + Math.random() * 90 | 0;
    g.fillStyle = `rgba(${v},${v},${v - 6},0.16)`;
    g.beginPath(); g.ellipse(x, y, r * (0.7 + Math.random() * 0.7), r, Math.random() * 3.14, 0, 7); g.fill();
    g.fillStyle = "rgba(255,255,255,0.13)";
    g.beginPath(); g.ellipse(x - r * 0.25, y - r * 0.3, r * 0.45, r * 0.35, 0, 0, 7); g.fill();
  }
  // cracks — branching, and dark enough to cut a real groove in the normal map
  g.lineCap = "round";
  for (let i = 0; i < 14; i++) {
    let x = Math.random() * ALBEDO, y = Math.random() * ALBEDO;
    for (let br = 0; br < 3; br++) {
      g.strokeStyle = `rgba(0,0,0,${0.1 + Math.random() * 0.14})`;
      g.lineWidth = 1 + Math.random() * 1.8;
      g.beginPath(); g.moveTo(x, y);
      let cx = x, cy = y;
      for (let s = 0; s < 8; s++) { cx += (Math.random() - 0.5) * 80; cy += (Math.random() - 0.5) * 80; g.lineTo(cx, cy); }
      g.stroke();
    }
  }
  return { albedo: c, bump: 2.0 };
}

/** Painted RENDERED PLASTER / STUCCO — the correct wall material for the
 *  village houses, arena walls and every other generic structure that until now
 *  wore the industrial `surface` (brushed metal + domed rivets). A sunlit
 *  village reading as a riveted steel warehouse was the last clearly-WRONG map
 *  material in the game.
 *
 *  The whole idea of plaster is that it is CONTINUOUS. A hand-floated render has
 *  no panel seams, no coursing joints and no rivets — the three cues that made
 *  `surface`/`trim`/masonry read as metal or stacked block. What it has instead
 *  is a soft, large-scale trowel-sweep mottle, a fine sand grain, the odd
 *  hairline settlement crack, and a faint damp/dirt wash near the splash line.
 *  Matte throughout (a painted wall has no specular hotspot); only the weathered
 *  patches read a touch rougher, and that whisper of contrast is the single
 *  "material" event on the sheet — which is exactly how plaster behaves.
 *
 *  Near-white base so a per-box vertex tint (a house painted cream, ochre or
 *  pale blue) multiplies through, like every other structural family. */
function makePlaster(): Layers {
  const { c, g } = cvs(ALBEDO);
  const { c: hc, g: hg } = cvs(ALBEDO);
  const { c: rc, g: rg } = cvs(ROUGH);
  g.fillStyle = "#ece9e3"; g.fillRect(0, 0, ALBEDO, ALBEDO);
  hg.fillStyle = "#888888"; hg.fillRect(0, 0, ALBEDO, ALBEDO); // mid: gentle relief both ways
  rg.fillStyle = "#c6c6c6"; rg.fillRect(0, 0, ROUGH, ROUGH);   // matte painted render
  noise(g, 60000, 0.05);   // fine sand grain — dense but faint
  // TROWEL SWEEP — broad, soft, overlapping arcs of slightly lighter/darker
  // render. This is the signature of a hand-floated wall, and the one thing a
  // flat fill can never have. Drawn into BOTH albedo (tone) and the height
  // field (a gentle undulation the normal map turns into a soft sheen break).
  for (let i = 0; i < 90; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 70 + Math.random() * 190;
    const light = Math.random() < 0.5, a = 0.05 + Math.random() * 0.06;
    const grd = g.createRadialGradient(x, y, 0, x, y, r);
    grd.addColorStop(0, light ? `rgba(255,252,244,${a})` : `rgba(150,143,130,${a})`);
    grd.addColorStop(1, "rgba(0,0,0,0)");
    g.fillStyle = grd; g.fillRect(x - r, y - r, r * 2, r * 2);
    const hgrd = hg.createRadialGradient(x, y, 0, x, y, r);
    hgrd.addColorStop(0, light ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.10)");
    hgrd.addColorStop(1, "rgba(128,128,128,0)");
    hg.fillStyle = hgrd; hg.fillRect(x - r, y - r, r * 2, r * 2);
  }
  // HAIRLINE SETTLEMENT CRACKS — a handful, thin, branching, cutting a shallow
  // groove in the height field. Sparse: plaster is mostly whole.
  g.lineCap = "round"; hg.lineCap = "round";
  for (let i = 0; i < 7; i++) {
    const x0 = Math.random() * ALBEDO, y0 = Math.random() * ALBEDO;
    for (let br = 0, branches = 1 + (Math.random() * 2 | 0); br < branches; br++) {
      let cx = x0, cy = y0;
      const pts: [number, number][] = [[cx, cy]];
      for (let s = 0, steps = 5 + (Math.random() * 5 | 0); s < steps; s++) {
        cx += (Math.random() - 0.5) * 46; cy += (Math.random() - 0.4) * 60; pts.push([cx, cy]);
      }
      g.strokeStyle = `rgba(70,66,58,${0.10 + Math.random() * 0.12})`; g.lineWidth = 0.8 + Math.random() * 1.1;
      g.beginPath(); g.moveTo(pts[0][0], pts[0][1]); for (const [px, py] of pts) g.lineTo(px, py); g.stroke();
      hg.strokeStyle = "rgba(0,0,0,0.40)"; hg.lineWidth = 1.4;
      hg.beginPath(); hg.moveTo(pts[0][0], pts[0][1]); for (const [px, py] of pts) hg.lineTo(px, py); hg.stroke();
    }
  }
  // DAMP / DIRT WASH — soft blobs of grime plaster picks up from splashback and
  // rising damp. Blobs, not a hard band, so a tall wall does not repeat stripes.
  // The one place the surface reads ROUGHER, so the wash also drives roughness.
  for (let i = 0; i < 26; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 24 + Math.random() * 70;
    const grd = g.createRadialGradient(x, y, 0, x, y, r);
    grd.addColorStop(0, `rgba(120,112,98,${0.03 + Math.random() * 0.05})`);
    grd.addColorStop(1, "rgba(0,0,0,0)");
    g.fillStyle = grd; g.fillRect(x - r, y - r, r * 2, r * 2);
    const rr = r / 4;
    const rgrd = rg.createRadialGradient(x / 4, y / 4, 0, x / 4, y / 4, rr);
    rgrd.addColorStop(0, "rgba(255,255,255,0.40)"); rgrd.addColorStop(1, "rgba(255,255,255,0)");
    rg.fillStyle = rgrd; rg.fillRect(x / 4 - rr, y / 4 - rr, rr * 2, rr * 2);
  }
  // SPALLED CHIPS — sparse, tiny pocks where the render has flaked to a darker
  // scratch coat, each with a lit lower rim. Reads at close range, adds no tile.
  for (let i = 0; i < 90; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 0.8 + Math.random() * 2.6;
    g.fillStyle = `rgba(150,140,124,${0.10 + Math.random() * 0.14})`;
    g.beginPath(); g.ellipse(x, y, r * (0.7 + Math.random() * 0.6), r, Math.random() * 3.14, 0, 7); g.fill();
    g.fillStyle = "rgba(255,255,255,0.10)";
    g.beginPath(); g.ellipse(x, y + r * 0.7, r * 0.6, r * 0.3, 0, 0, 7); g.fill();
    hg.fillStyle = "rgba(0,0,0,0.28)"; hg.beginPath(); hg.arc(x, y, r, 0, 7); hg.fill();
  }
  return { albedo: c, height: hc, rough: rc, bump: 1.7 };
}

/** Architectural TRIM: cast stone / painted concrete moulding.
 *
 *  This is the surface every cornice, pilaster, plinth, belt course, parapet,
 *  window mullion and door surround in the game wears. Until now they all wore
 *  `surface` — brushed metal with domed rivets — which on a stone castle or a
 *  concrete plaza is simply the wrong material, and on a building-dense map it
 *  is a large fraction of the screen.
 *
 *  The signature detail is the VERTICAL WEATHER DRIP. Real trim is what rain
 *  runs off, so it carries dark streaks below every ledge; without them cast
 *  stone reads as painted cardboard no matter how fine its grain is. The
 *  streaks also drive the roughness map (wet-weathered stone is rougher than
 *  the clean face), which is what gives the family its material contrast
 *  against the smooth `facade` panels it sits on.
 *
 *  Tiles at 128 px = one coping unit, so the joint lines land on the same
 *  lattice the other families use. */
function makeTrim(): Layers {
  const { c, g } = cvs(ALBEDO);
  const { c: hc, g: hg } = cvs(ALBEDO);
  const { c: rc, g: rg } = cvs(ROUGH);
  g.fillStyle = "#e9e7e1"; g.fillRect(0, 0, ALBEDO, ALBEDO);
  hg.fillStyle = "#a0a0a0"; hg.fillRect(0, 0, ALBEDO, ALBEDO);
  rg.fillStyle = "#b4b4b4"; rg.fillRect(0, 0, ROUGH, ROUGH); // clean cast stone
  noise(g, 46000, 0.13);
  // cast-stone mottle: broad tonal patches, the thing a flat fill never has
  for (let i = 0; i < 46; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 30 + Math.random() * 110;
    const grd = g.createRadialGradient(x, y, 0, x, y, r);
    grd.addColorStop(0, `rgba(${118 + Math.random() * 60 | 0},${115 + Math.random() * 55 | 0},${104 + Math.random() * 50 | 0},0.09)`);
    grd.addColorStop(1, "rgba(0,0,0,0)");
    g.fillStyle = grd; g.fillRect(x - r, y - r, r * 2, r * 2);
  }
  // unit joints every 128 px — cut IN (dark in albedo, dark in height) with a
  // lit lower lip, so a long cornice reads as laid stones and not as extrusion
  for (const p of [0, 128, 256, 384]) {
    g.fillStyle = "rgba(70,66,58,0.30)"; g.fillRect(0, p + 2, ALBEDO, 3);
    g.fillStyle = "rgba(255,255,255,0.16)"; g.fillRect(0, p + 5, ALBEDO, 2);
    hg.fillStyle = "#4c4c4c"; hg.fillRect(0, p + 2, ALBEDO, 4);
    // vertical joints, staggered per course so the courses don't line up
    for (let k = 0; k < 3; k++) {
      const vx = ((p / 128) % 2 ? 64 : 0) + k * 170 + 20;
      g.fillStyle = "rgba(70,66,58,0.24)"; g.fillRect(vx, p + 5, 3, 123);
      hg.fillStyle = "#565656"; hg.fillRect(vx, p + 5, 4, 123);
    }
  }
  // VERTICAL WEATHER DRIPS — dark streaks running down from each joint. Widths
  // and lengths vary; a few are strong, most are a whisper. These also become
  // the rough streaks below.
  for (let i = 0; i < 190; i++) {
    const x = Math.random() * ALBEDO;
    const top = [0, 128, 256, 384][(Math.random() * 4) | 0] + 6;
    const len = 20 + Math.random() * 118;
    const w = 1.5 + Math.random() * 7;
    const a = 0.035 + Math.random() * 0.075;
    const grd = g.createLinearGradient(0, top, 0, top + len);
    grd.addColorStop(0, `rgba(74,70,60,${a})`);
    grd.addColorStop(0.35, `rgba(74,70,60,${a * 0.8})`);
    grd.addColorStop(1, "rgba(74,70,60,0)");
    g.fillStyle = grd; g.fillRect(x, top, w, len);
    // matching roughness streak (ROUGH is 1/4 scale)
    const rgrd = rg.createLinearGradient(0, top / 4, 0, (top + len) / 4);
    rgrd.addColorStop(0, "rgba(255,255,255,0.55)");
    rgrd.addColorStop(1, "rgba(255,255,255,0)");
    rg.fillStyle = rgrd; rg.fillRect(x / 4, top / 4, Math.max(1, w / 4), len / 4);
  }
  // chips and pits along the joints — small, dark, with a lit rim below
  for (let i = 0; i < 260; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 0.9 + Math.random() * 3.2;
    g.fillStyle = `rgba(88,84,74,${0.10 + Math.random() * 0.16})`;
    g.beginPath(); g.ellipse(x, y, r * (0.6 + Math.random() * 0.8), r, Math.random() * 3.14, 0, 7); g.fill();
    g.fillStyle = "rgba(255,255,255,0.12)";
    g.beginPath(); g.ellipse(x, y + r * 0.7, r * 0.7, r * 0.3, 0, 0, 7); g.fill();
    hg.fillStyle = "rgba(0,0,0,0.35)";
    hg.beginPath(); hg.arc(x, y, r, 0, 7); hg.fill();
  }
  return { albedo: c, height: hc, rough: rc, bump: 1.9 };
}

/** Building facade: panel wall + recessed window grid (tileable).
 *  Returns the albedo canvas and a matching emissive canvas where a random
 *  subset of windows is LIT (black elsewhere) — buildings read as buildings,
 *  not stacked crates. The height field makes the reveals genuinely recessed
 *  and the spandrel bands genuinely proud, which is what gives a facade its
 *  shadow line at a grazing angle. */
/** Facade window layout, shared with pbrTextures' photo underlay composite
 *  (which must restore exactly these glass panes untextured). */
export const FACADE_LAYOUT = {
  cells: [0, 128, 256, 384] as readonly number[],
  glass: { x: 34, y: 32, w: 60, h: 68 },
  size: ALBEDO,
};
function makeFacade(): { layers: Layers; glow: HTMLCanvasElement } {
  const { c: albedo, g: a } = cvs(ALBEDO);
  const { c: hc, g: hg } = cvs(ALBEDO);
  const { c: glow, g } = cvs(ALBEDO);
  // Dedicated roughness. The derived-from-albedo fallback got this exactly
  // BACKWARDS: it maps dark -> rough, and the glass pane is the darkest thing
  // on the sheet, so every window came out ROUGHER than the wall around it —
  // which is why a facade never read as glazed no matter how good the albedo
  // got. Glass is the one genuinely glossy surface on a building.
  const { c: rc, g: rg } = cvs(ROUGH);
  a.fillStyle = "#e6e6e6"; a.fillRect(0, 0, ALBEDO, ALBEDO);
  hg.fillStyle = "#9a9a9a"; hg.fillRect(0, 0, ALBEDO, ALBEDO); // wall plane
  rg.fillStyle = "#d2d2d2"; rg.fillRect(0, 0, ROUGH, ROUGH); // painted panel: rough
  noise(a, 18000, 0.1);
  g.fillStyle = "#000"; g.fillRect(0, 0, ALBEDO, ALBEDO);
  const cells = FACADE_LAYOUT.cells;
  const GL = FACADE_LAYOUT.glass;
  for (const cx of cells) {
    for (const cy of cells) {
      // deep reveal around the opening (the wall steps BACK into the glass)
      hg.fillStyle = "#5a5a5a"; hg.fillRect(cx + 22, cy + 20, 84, 92);
      a.fillStyle = "rgba(0,0,0,0.30)"; a.fillRect(cx + 22, cy + 20, 84, 92);
      // mullioned frame — proud of the glass
      hg.fillStyle = "#c8c8c8";
      hg.fillRect(cx + 28, cy + 26, 72, 6); hg.fillRect(cx + 28, cy + 100, 72, 6);
      hg.fillRect(cx + 28, cy + 26, 6, 80); hg.fillRect(cx + 94, cy + 26, 6, 80);
      hg.fillRect(cx + 61, cy + 30, 5, 72);
      a.fillStyle = "rgba(170,198,222,0.60)"; a.fillRect(cx + GL.x, cy + GL.y, GL.w, GL.h); // glass
      // sky-reflection streak across the pane
      a.fillStyle = "rgba(255,255,255,0.20)";
      a.beginPath();
      a.moveTo(cx + 34, cy + 86); a.lineTo(cx + 66, cy + 32); a.lineTo(cx + 82, cy + 32); a.lineTo(cx + 46, cy + 100);
      a.closePath(); a.fill();
      a.fillStyle = "rgba(0,0,0,0.32)"; a.fillRect(cx + 61, cy + 32, 5, 68); // center mullion
      // glass: near-mirror. Frame/mullion: satin metal. (ROUGH is 1/4 scale.)
      rg.fillStyle = "#1e1e1e"; rg.fillRect((cx + 34) / 4, (cy + 32) / 4, 60 / 4, 68 / 4);
      rg.fillStyle = "#8c8c8c"; rg.fillRect((cx + 28) / 4, (cy + 26) / 4, 72 / 4, 6 / 4);
      rg.fillStyle = "#8c8c8c"; rg.fillRect((cx + 28) / 4, (cy + 100) / 4, 72 / 4, 6 / 4);
      // sill: a proud ledge under every window, the classic facade shadow line
      rg.fillStyle = "#e8e8e8"; rg.fillRect((cx + 18) / 4, (cy + 106) / 4, 92 / 4, 9 / 4); // weathered sill
      hg.fillStyle = "#ffffff"; hg.fillRect(cx + 18, cy + 106, 92, 9);
      a.fillStyle = "rgba(255,255,255,0.16)"; a.fillRect(cx + 18, cy + 106, 92, 5);
      a.fillStyle = "rgba(0,0,0,0.22)"; a.fillRect(cx + 18, cy + 113, 92, 4);
      if (Math.random() < 0.4) {
        g.fillStyle = ["#ffd9a0", "#ffeecc", "#bfe3ff"][(Math.random() * 3) | 0];
        g.fillRect(cx + GL.x, cy + GL.y, GL.w, GL.h);
      }
    }
  }
  // spandrel band between window rows — raised
  for (const cy of cells) {
    a.fillStyle = "rgba(0,0,0,0.15)"; a.fillRect(0, cy + 118, ALBEDO, 9);
    hg.fillStyle = "#d2d2d2"; hg.fillRect(0, cy + 118, ALBEDO, 9);
    a.fillStyle = "rgba(255,255,255,0.10)"; a.fillRect(0, cy + 117, ALBEDO, 2);
  }
  return { layers: { albedo, height: hc, rough: rc, bump: 2.2 }, glow };
}

/** Organic mottled terrain for day-map grounds — kills the flat-slab look. */
/** NATURAL GROUND — grass, moss, dirt, sand. The single biggest surface in the
 *  daytime open maps (the castle field, canyon floor, island, sandlot, village
 *  outskirts), and until now the ONLY large surface with no dedicated height or
 *  roughness map at all: albedo mottling + a flat bump, so under a grazing
 *  camera it read as a flat green sheet — the "toy field" tell paving fixed for
 *  made ground but left untouched here.
 *
 *  The box COLOUR tints this (grass green / dirt brown / sand), so the albedo
 *  stays near-neutral and the realism lives in the two maps the shader lights:
 *   - HEIGHT → normal: clump MOUNDS (raised tufts catch light), trodden DIVOTS,
 *     bare-earth scuffs and scattered pebble bumps, over a low undulation. This
 *     is what gives the ground micro-relief instead of a mirror-flat plane.
 *   - ROUGH: dry grass is very rough; damp hollows and bare-earth patches are
 *     smoother, so moisture reads as a sheen change the albedo can't show.
 *  Luminance-only macro variation (never a strong hue) so it looks right whether
 *  the slab is grass-green or desert-tan. */
function makeTerrain(): Layers {
  const { c, g } = cvs(ALBEDO);
  const { c: hc, g: hg } = cvs(ALBEDO);
  const { c: rc, g: rg } = cvs(ROUGH);
  g.fillStyle = "#ded9cc"; g.fillRect(0, 0, ALBEDO, ALBEDO);
  hg.fillStyle = "#808080"; hg.fillRect(0, 0, ALBEDO, ALBEDO);       // mid = ground plane
  rg.fillStyle = "#efefef"; rg.fillRect(0, 0, ROUGH, ROUGH);         // dry grass = very rough
  noise(g, 64000, 0.16);
  // MACRO TONAL VARIATION — broad soft blooms: damp shade (darker), sun-dried
  // thatch (lighter), bare-earth wash (warm). Luminance only, so the box tint
  // still owns the hue. This is the large-scale tone a flat field never had.
  for (let i = 0; i < 9; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 130 + Math.random() * 220;
    const k = Math.random();
    const [col, ha] = k < 0.4 ? ["rgba(78,84,66,0.13)", -0.5]      // damp shade, recessed
      : k < 0.72 ? ["rgba(232,228,208,0.11)", 0.4]                 // sun-dried, proud
      : ["rgba(150,132,96,0.12)", -0.3];                           // bare earth
    const grd = g.createRadialGradient(x, y, 0, x, y, r);
    grd.addColorStop(0, col); grd.addColorStop(1, "rgba(0,0,0,0)");
    g.fillStyle = grd; g.fillRect(x - r, y - r, r * 2, r * 2);
    // the same bloom nudges the height field so tone and relief agree
    const hgd = hg.createRadialGradient(x, y, 0, x, y, r);
    hgd.addColorStop(0, `rgba(${ha < 0 ? "40,40,40" : "220,220,220"},${Math.abs(ha) * 0.28})`);
    hgd.addColorStop(1, "rgba(0,0,0,0)");
    hg.fillStyle = hgd; hg.fillRect(x - r, y - r, r * 2, r * 2);
  }
  // BARE PATCHES — where grass thins to trodden earth. Lighter/warmer in albedo,
  // sunk and much SMOOTHER in roughness (packed soil, not blades).
  for (let i = 0; i < 6; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 26 + Math.random() * 60;
    const grd = g.createRadialGradient(x, y, 0, x, y, r);
    grd.addColorStop(0, "rgba(158,140,104,0.22)"); grd.addColorStop(0.7, "rgba(158,140,104,0.10)"); grd.addColorStop(1, "rgba(0,0,0,0)");
    g.fillStyle = grd; g.fillRect(x - r, y - r, r * 2, r * 2);
    const hgd = hg.createRadialGradient(x, y, 0, x, y, r);
    hgd.addColorStop(0, "rgba(30,30,30,0.4)"); hgd.addColorStop(1, "rgba(0,0,0,0)");
    hg.fillStyle = hgd; hg.fillRect(x - r, y - r, r * 2, r * 2);
    const rr = r / (ALBEDO / ROUGH);
    const rgd = rg.createRadialGradient(x / (ALBEDO / ROUGH), y / (ALBEDO / ROUGH), 0, x / (ALBEDO / ROUGH), y / (ALBEDO / ROUGH), rr);
    rgd.addColorStop(0, "rgba(0,0,0,0.5)"); rgd.addColorStop(1, "rgba(0,0,0,0)"); // smoother
    rg.fillStyle = rgd; rg.fillRect(0, 0, ROUGH, ROUGH);
  }
  // GRASS CLUMPS — short blade strokes in tight groups (clumping is what reads
  // as ground, not static), each raised in the height field so the tuft catches
  // a highlight on its windward side.
  for (let clump = 0; clump < 150; clump++) {
    const bx = Math.random() * ALBEDO, by = Math.random() * ALBEDO;
    const dark = Math.random() < 0.5;
    // a soft mound under the clump
    const mr = 12 + Math.random() * 14;
    const hgd = hg.createRadialGradient(bx, by, 0, bx, by, mr);
    hgd.addColorStop(0, "rgba(210,210,210,0.5)"); hgd.addColorStop(1, "rgba(0,0,0,0)");
    hg.fillStyle = hgd; hg.fillRect(bx - mr, by - mr, mr * 2, mr * 2);
    for (let i = 0; i < 16; i++) {
      const x = bx + (Math.random() - 0.5) * 32, y = by + (Math.random() - 0.5) * 32;
      const len = 3 + Math.random() * 6;
      g.strokeStyle = dark ? "rgba(70,96,52,0.24)" : "rgba(196,200,168,0.22)";
      g.lineWidth = 1;
      g.beginPath(); g.moveTo(x, y); g.lineTo(x + (Math.random() - 0.5) * 5, y - len); g.stroke();
      // blade tips catch light in the height field (thin bright flecks)
      hg.strokeStyle = "rgba(235,235,235,0.5)"; hg.lineWidth = 1;
      hg.beginPath(); hg.moveTo(x, y); hg.lineTo(x + (Math.random() - 0.5) * 5, y - len); hg.stroke();
    }
  }
  // PEBBLES / grit — scattered stones with a lit crown, sunk into the soil.
  for (let i = 0; i < 720; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 1 + Math.random() * 3;
    g.fillStyle = "rgba(92,86,72,0.2)";
    g.beginPath(); g.ellipse(x, y, r * 1.3, r, Math.random() * 3.14, 0, 7); g.fill();
    g.fillStyle = "rgba(255,255,255,0.1)";
    g.beginPath(); g.ellipse(x - r * 0.3, y - r * 0.3, r * 0.5, r * 0.4, 0, 0, 7); g.fill();
    hg.fillStyle = "rgba(225,225,225,0.5)";
    hg.beginPath(); hg.ellipse(x, y, r * 1.2, r * 0.9, 0, 0, 7); hg.fill();
  }
  return { albedo: c, height: hc, rough: rc, bump: 2.2 };
}

/** PAVING — the made ground: streets, plazas, hangar floors, arena decks.
 *
 *  Until 2026-08-01 every flat-ground box in the game wore `terrain` (day) or
 *  `concrete` (neon). `terrain` is GRASS AND DIRT — it was being painted over
 *  downtown's streets, the compound's hangar floor and every arena deck, which
 *  is a large part of why urban maps read as toys: the biggest surface on
 *  screen was the wrong material. `concrete` is a plain slab with no unit
 *  structure at all, so at a grazing angle (which is how you always see the
 *  floor) it has nothing for the eye to measure distance against.
 *
 *  The three details that make made ground read as made ground:
 *   - SLAB JOINTS on a 256 px unit (≈3 m at the ground tile scale), cut into
 *     the height field with a lit lower lip, plus finer 128 px cross joints.
 *     This is the perspective grid a floor needs.
 *   - PATCH REPAIRS: darker asphalt rectangles with ragged edges, tar-sealed
 *     seams and their own grit. Real ground is repaired ground; unbroken
 *     paving everywhere is what a texture atlas looks like, not a street.
 */
function makePaving(): Layers {
  const { c, g } = cvs(ALBEDO);
  const { c: hc, g: hg } = cvs(ALBEDO);
  const { c: rc, g: rg } = cvs(ROUGH);
  g.fillStyle = "#dcd9d2"; g.fillRect(0, 0, ALBEDO, ALBEDO);
  hg.fillStyle = "#9a9a9a"; hg.fillRect(0, 0, ALBEDO, ALBEDO);
  rg.fillStyle = "#e8e8e8"; rg.fillRect(0, 0, ROUGH, ROUGH); // dry paving = rough
  noise(g, 62000, 0.15);
  // MACRO WEATHERING BLOOMS — broad, soft, low-frequency discolouration (dirt
  // wash, sun-bleach, damp shade) spread across the whole tile. Without these a
  // plaza reads as one flat grey sheet: the slab joints give it a grid but no
  // large-scale tone, which is the "texture atlas, not a real floor" tell that
  // survived every fine-detail pass. Drawn first so the aggregate + joints below
  // sit on top of the variation, not under it.
  for (let i = 0; i < 7; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 150 + Math.random() * 230;
    const dark = Math.random() < 0.62; // weathering darkens more often than it bleaches
    const a = 0.06 + Math.random() * 0.08;
    const grd = g.createRadialGradient(x, y, 0, x, y, r);
    grd.addColorStop(0, dark ? `rgba(96,92,84,${a})` : `rgba(240,238,230,${a})`);
    grd.addColorStop(1, "rgba(0,0,0,0)");
    g.fillStyle = grd; g.fillRect(x - r, y - r, r * 2, r * 2);
  }
  // per-slab tonal variation: cast slabs never cure to the same shade (stronger
  // now — a real pavement's slabs differ enough to read as separate castings)
  for (let sy = 0; sy < 2; sy++) {
    for (let sx = 0; sx < 2; sx++) {
      const v = 0.08 + Math.random() * 0.1;
      g.fillStyle = `rgba(${118 + Math.random() * 70 | 0},${116 + Math.random() * 66 | 0},${110 + Math.random() * 60 | 0},${v})`;
      g.fillRect(sx * 256, sy * 256, 256, 256);
    }
  }
  // exposed aggregate — same trick as makeConcrete, denser and smaller: this is
  // what stops a floor being a flat wash under a grazing camera
  for (let i = 0; i < 2200; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 0.9 + Math.random() * 3.2;
    const v = 95 + Math.random() * 95 | 0;
    g.fillStyle = `rgba(${v},${v},${v - 8},0.15)`;
    g.beginPath(); g.ellipse(x, y, r * (0.7 + Math.random() * 0.7), r, Math.random() * 3.14, 0, 7); g.fill();
    g.fillStyle = "rgba(255,255,255,0.12)";
    g.beginPath(); g.ellipse(x - r * 0.25, y - r * 0.3, r * 0.45, r * 0.35, 0, 0, 7); g.fill();
  }
  // PATCH REPAIRS — darker asphalt rectangles with a tar-sealed edge
  for (let i = 0; i < 5; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO;
    const w = 50 + Math.random() * 150, h = 40 + Math.random() * 120;
    g.fillStyle = `rgba(74,72,70,${0.10 + Math.random() * 0.09})`;
    g.fillRect(x, y, w, h);
    g.strokeStyle = "rgba(40,38,36,0.26)"; g.lineWidth = 2.5;
    g.strokeRect(x, y, w, h);
    hg.fillStyle = "rgba(255,255,255,0.10)"; hg.fillRect(x, y, w, h); // slightly proud
    rg.fillStyle = "rgba(255,255,255,0.35)"; rg.fillRect(x / 4, y / 4, w / 4, h / 4);
  }
  // SLAB JOINTS: 256 px units, with 128 px cross joints inside them
  const joint = (x: number, y: number, w: number, h: number, strong: boolean): void => {
    g.fillStyle = `rgba(64,62,58,${strong ? 0.34 : 0.2})`;
    g.fillRect(x, y, w, h);
    // lit lower lip — the highlight is what makes a groove read as a groove
    g.fillStyle = `rgba(255,255,255,${strong ? 0.15 : 0.09})`;
    g.fillRect(x + (w > h ? 0 : h), y + (w > h ? h : 0), w > h ? w : h, w > h ? h * 0.7 : h);
    hg.fillStyle = strong ? "#3c3c3c" : "#666666";
    hg.fillRect(x, y, w, h);
  };
  for (const p of [0, 256]) {
    joint(0, p, ALBEDO, 5, true);
    joint(p, 0, 5, ALBEDO, true);
  }
  for (const p of [128, 384]) {
    joint(0, p, ALBEDO, 3, false);
    joint(p, 0, 3, ALBEDO, false);
  }
  // grit collecting in the joints
  for (let i = 0; i < 900; i++) {
    const along = Math.random() * ALBEDO;
    const line = [0, 128, 256, 384][(Math.random() * 4) | 0] + (Math.random() - 0.5) * 11;
    const [x, y] = Math.random() < 0.5 ? [along, line] : [line, along];
    g.fillStyle = `rgba(${100 + Math.random() * 70 | 0},${96 + Math.random() * 66 | 0},${88 + Math.random() * 60 | 0},0.2)`;
    g.fillRect(x, y, 1 + Math.random() * 2, 1 + Math.random() * 2);
  }
  // hairline cracks radiating from the joints
  g.lineCap = "round";
  for (let i = 0; i < 22; i++) {
    let x = Math.random() * ALBEDO, y = Math.random() * ALBEDO;
    g.strokeStyle = `rgba(0,0,0,${0.08 + Math.random() * 0.12})`;
    g.lineWidth = 0.8 + Math.random() * 1.3;
    g.beginPath(); g.moveTo(x, y);
    for (let s = 0; s < 6; s++) { x += (Math.random() - 0.5) * 60; y += (Math.random() - 0.5) * 60; g.lineTo(x, y); }
    g.stroke();
    hg.strokeStyle = "rgba(0,0,0,0.5)"; hg.lineWidth = 2; hg.stroke();
  }
  // WORN TRAFFIC POLISH: broad patches where feet and tyres have burnished the
  // aggregate smooth. Albedo barely moves; roughness moves a lot — which is
  // exactly how polished stone behaves and is invisible in a texture viewer.
  for (let i = 0; i < 10; i++) {
    const x = Math.random() * ROUGH, y = Math.random() * ROUGH, r = 8 + Math.random() * 22;
    const grd = rg.createRadialGradient(x, y, 0, x, y, r);
    grd.addColorStop(0, "rgba(0,0,0,0.22)");
    grd.addColorStop(1, "rgba(0,0,0,0)");
    rg.fillStyle = grd; rg.fillRect(x - r, y - r, r * 2, r * 2);
  }
  return { albedo: c, height: hc, rough: rc, bump: 2.2 };
}

/** Pixelated Minecraft-style block grain. It stays near-white so imported
 * block colors still identify stone/grass/wood, while the grid/noise makes
 * every top face read as voxel texture instead of smooth Neon Purge concrete. */
function makeMinecraftBlock(): Layers {
  const { c, g } = cvs(ALBEDO);
  g.imageSmoothingEnabled = false;
  g.fillStyle = "#e7e7e1"; g.fillRect(0, 0, ALBEDO, ALBEDO);
  const px = ALBEDO / 16;
  for (let y = 0; y < 16; y++) {
    for (let x = 0; x < 16; x++) {
      const v = 204 + ((x * 17 + y * 29 + ((x ^ y) * 11)) % 42);
      g.fillStyle = `rgb(${v},${v},${Math.max(175, v - 8)})`;
      g.fillRect(x * px, y * px, px, px);
    }
  }
  g.strokeStyle = "rgba(0,0,0,0.18)"; g.lineWidth = 4;
  for (let p = 0; p <= ALBEDO; p += px) {
    g.beginPath(); g.moveTo(p, 0); g.lineTo(p, ALBEDO); g.moveTo(0, p); g.lineTo(ALBEDO, p); g.stroke();
  }
  g.strokeStyle = "rgba(255,255,255,0.24)"; g.lineWidth = 2;
  for (let p = 2; p < ALBEDO; p += px) {
    g.beginPath(); g.moveTo(p, 0); g.lineTo(p, ALBEDO); g.moveTo(0, p); g.lineTo(ALBEDO, p); g.stroke();
  }
  return { albedo: c, bump: 1.0 };
}

/** Rough stone with layered mineral veins and individual coursed blocks. Used
 * for Minecraft/imported castle blocks so the build reads as masonry. */
function makeStone(): Layers {
  const { c, g } = cvs(ALBEDO);
  const { c: hc, g: hg } = cvs(ALBEDO);
  g.fillStyle = "#deded8"; g.fillRect(0, 0, ALBEDO, ALBEDO);
  hg.fillStyle = "#b4b4b4"; hg.fillRect(0, 0, ALBEDO, ALBEDO);
  noise(g, 72000, 0.12);
  // individual ashlar blocks, each with its own tone and a chipped top edge
  const rows = 8, rh = ALBEDO / rows;
  for (let r = 0; r < rows; r++) {
    const off = (r % 2) * 46;
    for (let x = -off; x < ALBEDO; x += 92) {
      const w = 92, y = r * rh;
      const tone = 208 + ((r * 37 + x * 13) % 34);
      g.fillStyle = `rgba(${tone},${tone},${tone - 4},0.5)`;
      g.fillRect(x + 3, y + 3, w - 6, rh - 6);
      const hv = 150 + ((r * 53 + x * 29) % 60);
      hg.fillStyle = `rgb(${hv},${hv},${hv})`;
      hg.fillRect(x + 3, y + 3, w - 6, rh - 6);
      // weathered upper-left highlight / lower-right shade on each stone
      g.fillStyle = "rgba(255,255,255,0.14)"; g.fillRect(x + 3, y + 3, w - 6, 3);
      g.fillStyle = "rgba(0,0,0,0.14)"; g.fillRect(x + 3, y + rh - 8, w - 6, 4);
    }
  }
  // mortar joints — recessed
  hg.fillStyle = "#3c3c3c";
  for (let r = 0; r <= rows; r++) hg.fillRect(0, r * rh - 3, ALBEDO, 6);
  g.fillStyle = "rgba(60,58,52,0.30)";
  for (let r = 0; r <= rows; r++) g.fillRect(0, r * rh - 3, ALBEDO, 6);
  for (let r = 0; r < rows; r++) {
    const off = (r % 2) * 46;
    for (let x = -off; x < ALBEDO; x += 92) {
      hg.fillStyle = "#3c3c3c"; hg.fillRect(x - 3, r * rh, 6, rh);
      g.fillStyle = "rgba(60,58,52,0.30)"; g.fillRect(x - 3, r * rh, 6, rh);
    }
  }
  // mineral veins + pitting across the courses
  for (let i = 0; i < 70; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO;
    g.strokeStyle = Math.random() < 0.5 ? "rgba(255,255,255,0.14)" : "rgba(55,55,55,0.12)";
    g.lineWidth = 1 + Math.random();
    g.beginPath(); g.moveTo(x, y); g.lineTo(x + 30 + Math.random() * 60, y + (Math.random() - 0.5) * 24); g.stroke();
  }
  for (let i = 0; i < 500; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 1 + Math.random() * 2.5;
    g.fillStyle = "rgba(40,40,38,0.16)"; g.beginPath(); g.arc(x, y, r, 0, 7); g.fill();
    hg.fillStyle = "rgba(0,0,0,0.30)"; hg.beginPath(); hg.arc(x, y, r, 0, 7); hg.fill();
  }
  return { albedo: c, height: hc, bump: 2.4 };
}

/** Tileable brick courses for walls and castles. */
function makeBrick(): Layers {
  const { c, g } = cvs(ALBEDO);
  const { c: hc, g: hg } = cvs(ALBEDO);
  g.fillStyle = "#d8d1c4"; g.fillRect(0, 0, ALBEDO, ALBEDO);
  hg.fillStyle = "#303030"; hg.fillRect(0, 0, ALBEDO, ALBEDO); // mortar plane (low)
  noise(g, 36000, 0.1);
  const rh = 64, bw = 136;
  for (let y = 0; y < ALBEDO; y += rh) {
    const off = ((y / rh) % 2) * (bw / 2);
    for (let x = -off; x < ALBEDO; x += bw) {
      const t = 200 + ((x * 7 + y * 13) % 46);
      g.fillStyle = `rgba(${t},${t - 12},${t - 26},0.55)`;
      g.fillRect(x + 5, y + 5, bw - 10, rh - 10);
      const hv = 190 + ((x * 11 + y * 5) % 50); // brick face sits proud of the mortar

      hg.fillStyle = `rgb(${hv},${hv},${hv})`;
      hg.fillRect(x + 5, y + 5, bw - 10, rh - 10);
      g.fillStyle = "rgba(255,255,255,0.15)"; g.fillRect(x + 5, y + 5, bw - 10, 3);
      g.fillStyle = "rgba(0,0,0,0.16)"; g.fillRect(x + 5, y + rh - 8, bw - 10, 3);
      // per-brick speckle
      for (let s = 0; s < 26; s++) {
        g.fillStyle = Math.random() < 0.5 ? "rgba(255,255,255,0.09)" : "rgba(60,40,30,0.10)";
        g.fillRect(x + 6 + Math.random() * (bw - 12), y + 6 + Math.random() * (rh - 12), 2, 2);
      }
    }
  }
  return { albedo: c, height: hc, bump: 2.8 };
}

/** WOOD — sawn plank cladding: the material a timber building and a ship's hull
 *  are actually made of. Until now the two wooden maps (pirate_ship, the wild
 *  west) wore brown-tinted `plaster`, so a ship's hull and a saloon front read
 *  as smooth painted stucco with no boards at all. The signature is the STRAKE:
 *  horizontal boards separated by a hard shadow gap. worldProjectUV maps the
 *  texture's V to world-up on a wall, so the horizontal bands land as horizontal
 *  planks — clapboard on a building, hull strakes on the galleon. Everything
 *  else (grain, knots, checking, the cupped board relief) exists to keep the
 *  boards from reading as painted stripes:
 *   - the GAP between boards is cut deep into the height field (dark groove) and
 *     the roughness (a shadowed crevice is dust-matte) — this is the whole read;
 *   - each board is slightly CUPPED (lit centre, darker edges in height) so it
 *     catches the sun as a rounded plank, not a flat decal;
 *   - long horizontal GRAIN streaks + a few KNOTS with concentric rings give the
 *     close-up read; end CHECKS (short splits) weather it.
 *  Near-light warm base so the per-box vertex tint multiplies through (a grey
 *  box → weathered barn-grey timber; a brown hull → oiled brown planking). */
function makeWood(): Layers {
  const { c, g } = cvs(ALBEDO);
  const { c: hc, g: hg } = cvs(ALBEDO);
  const { c: rc, g: rg } = cvs(ROUGH);
  g.fillStyle = "#cdb488"; g.fillRect(0, 0, ALBEDO, ALBEDO);
  hg.fillStyle = "#9a9a9a"; hg.fillRect(0, 0, ALBEDO, ALBEDO);
  rg.fillStyle = "#c4c4c4"; rg.fillRect(0, 0, ROUGH, ROUGH);   // wood is matte
  noise(g, 40000, 0.05);   // fine fibre grain
  const PLANK = ALBEDO / 6;  // ~0.53 m boards at the 3.2 m wall repeat
  const GAP = 5;
  for (let p = 0; p < 6; p++) {
    const y0 = p * PLANK;
    // per-board tone: alternate warmer / cooler / greyer sawn boards
    const warm = (p * 53) % 3;
    const dr = warm === 0 ? 12 : warm === 1 ? -8 : -20;
    g.fillStyle = `rgba(${200 + dr},${176 + dr},${136 + dr * 0.6},0.26)`;
    g.fillRect(0, y0 + GAP, ALBEDO, PLANK - GAP);
    // CUP: a soft vertical gradient across the board — lit centre, shaded edges,
    // baked into the height field so the normal map rounds each plank.
    const cg = hg.createLinearGradient(0, y0 + GAP, 0, y0 + PLANK);
    cg.addColorStop(0, "rgba(70,70,70,0.55)");   // top edge: low
    cg.addColorStop(0.5, "rgba(180,180,180,0.5)"); // crown: high
    cg.addColorStop(1, "rgba(70,70,70,0.55)");   // bottom edge: low
    hg.fillStyle = cg; hg.fillRect(0, y0 + GAP, ALBEDO, PLANK - GAP);
    // the GAP between boards: hard dark shadow line in albedo, a deep trench in
    // the height field, and the roughest thing on the surface.
    g.fillStyle = "rgba(38,22,10,0.5)"; g.fillRect(0, y0, ALBEDO, GAP);
    hg.fillStyle = "#161616"; hg.fillRect(0, y0 - 1, ALBEDO, GAP + 2);
    const ry = (y0 / ALBEDO) * ROUGH;
    rg.fillStyle = "#ffffff"; rg.fillRect(0, ry - 1, ROUGH, (GAP / ALBEDO) * ROUGH + 2);
    // long GRAIN streaks running the length of the board (a few per board)
    for (let s = 0; s < 5; s++) {
      const gy = y0 + GAP + 4 + Math.random() * (PLANK - GAP - 8);
      g.strokeStyle = `rgba(72,42,18,${0.06 + Math.random() * 0.12})`;
      g.lineWidth = 0.8 + Math.random() * 1.6;
      g.beginPath();
      for (let x = 0; x <= ALBEDO; x += 14) g.lineTo(x, gy + Math.sin(x * 0.02 + p * 2) * 2.6);
      g.stroke();
    }
    // occasional KNOT: concentric rings, darker, a shallow pit in the height
    if (Math.random() < 0.6) {
      const kx = Math.random() * ALBEDO, ky = y0 + PLANK * (0.3 + Math.random() * 0.4);
      const ang = Math.random() * Math.PI;
      for (let k = 0; k < 4; k++) {
        g.strokeStyle = `rgba(58,32,14,${0.3 - k * 0.05})`; g.lineWidth = 2 - k * 0.3;
        g.beginPath(); g.ellipse(kx, ky, 4 + k * 6, 2.5 + k * 4, ang, 0, 7); g.stroke();
      }
      g.fillStyle = "rgba(44,24,10,0.4)"; g.beginPath(); g.ellipse(kx, ky, 4, 2.5, ang, 0, 7); g.fill();
      hg.fillStyle = "rgba(0,0,0,0.35)"; hg.beginPath(); hg.ellipse(kx, ky, 5, 3.2, ang, 0, 7); hg.fill();
    }
    // END CHECK: a short weather split running INTO the board from an end
    if (Math.random() < 0.5) {
      const ex = Math.random() < 0.5 ? 0 : ALBEDO;
      const len = 30 + Math.random() * 70, dir = ex === 0 ? 1 : -1;
      const ey = y0 + PLANK * (0.35 + Math.random() * 0.3);
      g.strokeStyle = "rgba(30,18,8,0.42)"; g.lineWidth = 1;
      g.beginPath(); g.moveTo(ex, ey);
      for (let x = 0; x <= len; x += 10) g.lineTo(ex + dir * x, ey + Math.sin(x * 0.2) * 1.4);
      g.stroke();
      hg.strokeStyle = "rgba(0,0,0,0.32)"; hg.lineWidth = 1.4; hg.stroke();
    }
  }
  return { albedo: c, height: hc, rough: rc, bump: 1.7 };
}

/** PER-BLOCK VARIATION (2026-08-10, "you can do better"): Minecraft surfaces
 *  read as thousands of blocks because every block carries its own slight tint.
 *  Our merge collapses same-colour cells into one flat slab, so the variation
 *  must come back in the TEXTURE: stamp an 8×8 grid of 1m cells (at tile 8)
 *  with deterministic luminance jitter + a soft dark seam. Applied over each
 *  mc family's material pattern. */
function blockCells(c: HTMLCanvasElement, jitter: number, border = 0.16): HTMLCanvasElement {
  const g = c.getContext("2d", { willReadFrequently: true })!;
  const cells = 8;
  const s = c.width / cells;
  let seed = 0x5f3a91;
  const rnd = (): number => (seed = (seed * 1103515245 + 12345) & 0x7fffffff) / 0x7fffffff;
  for (let cy = 0; cy < cells; cy++) for (let cx = 0; cx < cells; cx++) {
    const v = (rnd() - 0.5) * 2 * jitter;
    g.fillStyle = v >= 0 ? `rgba(255,255,255,${v.toFixed(3)})` : `rgba(18,22,16,${(-v).toFixed(3)})`;
    g.fillRect(cx * s, cy * s, s, s);
  }
  g.strokeStyle = `rgba(0,0,0,${border})`;
  g.lineWidth = 2;
  for (let p = 0; p <= cells; p++) {
    g.beginPath(); g.moveTo(p * s, 0); g.lineTo(p * s, c.height); g.moveTo(0, p * s); g.lineTo(c.width, p * s); g.stroke();
  }
  return c;
}

/** Minecraft-scale brick: 4 courses per 1m cell (16px at 8 cells/512). */
function makeMcBrick(): Layers {
  const { c, g } = cvs(ALBEDO);
  g.fillStyle = "#d8d1c4"; g.fillRect(0, 0, ALBEDO, ALBEDO);
  noise(g, 30000, 0.08);
  const rh = 16, bw = 32;
  for (let y = 0; y < ALBEDO; y += rh) {
    const off = ((y / rh) % 2) * (bw / 2);
    for (let x = -off; x < ALBEDO; x += bw) {
      const t = 205 + ((x * 7 + y * 13) % 40);
      g.fillStyle = `rgba(${t},${t - 10},${t - 22},0.5)`;
      g.fillRect(x + 1, y + 1, bw - 2, rh - 2);
    }
    g.fillStyle = "rgba(40,34,30,0.35)"; g.fillRect(0, y, ALBEDO, 1.5);
  }
  return { albedo: c, bump: 1.6 };
}

/** Minecraft-scale planks: 4 strips per 1m cell with staggered joints. */
function makeMcWood(): Layers {
  const { c, g } = cvs(ALBEDO);
  g.fillStyle = "#d2b48b"; g.fillRect(0, 0, ALBEDO, ALBEDO);
  noise(g, 24000, 0.08);
  const sh = 16;
  for (let y = 0; y < ALBEDO; y += sh) {
    const t = 208 + ((y * 17) % 34);
    g.fillStyle = `rgba(${t},${t - 24},${t - 56},0.32)`;
    g.fillRect(0, y + 1, ALBEDO, sh - 2);
    g.fillStyle = "rgba(48,26,10,0.4)"; g.fillRect(0, y, ALBEDO, 1.5);
    // staggered end-joints
    const row = y / sh;
    for (let x = ((row * 23) % 64); x < ALBEDO; x += 64) {
      g.fillStyle = "rgba(48,26,10,0.35)"; g.fillRect(x, y, 1.5, sh);
    }
  }
  // light grain
  for (let y = 4; y < ALBEDO; y += 9) {
    g.strokeStyle = `rgba(65,34,14,${0.08 + ((y * 7) % 10) / 100})`;
    g.lineWidth = 1;
    g.beginPath();
    for (let x = 0; x <= ALBEDO; x += 14) g.lineTo(x, y + Math.sin(x * 0.04 + y) * 2);
    g.stroke();
  }
  return { albedo: c, bump: 1.4 };
}

/** Minecraft-scale leaves/grass: fine deterministic pixel noise (8px texels)
 *  with scattered darker holes — the old cluster smears were drawn for ~2m
 *  repeats and at the new 8m block-grid tile they read as camo marble across
 *  every tree and lawn. */
function makeMcLeaves(): Layers {
  const { c, g } = cvs(ALBEDO);
  g.fillStyle = "#c9d8b2"; g.fillRect(0, 0, ALBEDO, ALBEDO);
  const px = 8;
  let seed = 0x77aa11;
  const rnd = (): number => (seed = (seed * 1103515245 + 12345) & 0x7fffffff) / 0x7fffffff;
  for (let y = 0; y < ALBEDO; y += px) for (let x = 0; x < ALBEDO; x += px) {
    const v = 150 + rnd() * 100;
    g.fillStyle = `rgb(${(v * 0.84) | 0},${v | 0},${(v * 0.74) | 0})`;
    g.fillRect(x, y, px, px);
  }
  for (let i = 0; i < 900; i++) {
    g.fillStyle = "rgba(20,30,16,0.45)";
    g.fillRect(((rnd() * ALBEDO) | 0) & ~7, ((rnd() * ALBEDO) | 0) & ~7, px, px);
  }
  return { albedo: c, bump: 1.2 };
}

function makeFoliage(): Layers {
  const { c, g } = cvs(ALBEDO);
  g.fillStyle = "#dbe8c8"; g.fillRect(0, 0, ALBEDO, ALBEDO);
  noise(g, 48000, 0.1);
  // overlapping leaf clusters at two scales — depth instead of a flat mottle
  for (const [n, rmin, rmax, alpha] of [[220, 12, 34, 0.16], [520, 4, 12, 0.2]] as const) {
    for (let i = 0; i < n; i++) {
      const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = rmin + Math.random() * (rmax - rmin);
      g.fillStyle = Math.random() < 0.5 ? `rgba(48,96,44,${alpha})` : `rgba(174,206,116,${alpha})`;
      g.beginPath(); g.ellipse(x, y, r * 1.5, r * 0.7, Math.random() * Math.PI, 0, Math.PI * 2); g.fill();
    }
  }
  // a few bright rim-lit leaf edges
  for (let i = 0; i < 260; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO;
    g.strokeStyle = "rgba(226,248,180,0.22)"; g.lineWidth = 1;
    g.beginPath(); g.ellipse(x, y, 8 + Math.random() * 10, 3 + Math.random() * 4, Math.random() * Math.PI, 0, Math.PI); g.stroke();
  }
  return { albedo: c, bump: 1.6 };
}

function makeEarth(): Layers {
  const { c, g } = cvs(ALBEDO);
  g.fillStyle = "#d7c1a2"; g.fillRect(0, 0, ALBEDO, ALBEDO);
  noise(g, 60000, 0.15);
  for (let i = 0; i < 140; i++) {
    g.fillStyle = Math.random() < 0.5 ? "rgba(110,75,45,0.16)" : "rgba(80,115,55,0.1)";
    g.beginPath(); g.arc(Math.random() * ALBEDO, Math.random() * ALBEDO, 6 + Math.random() * 30, 0, Math.PI * 2); g.fill();
  }
  // clods + embedded pebbles
  for (let i = 0; i < 900; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 1.5 + Math.random() * 4;
    g.fillStyle = "rgba(92,70,46,0.22)";
    g.beginPath(); g.ellipse(x, y, r * 1.2, r, Math.random() * 3.14, 0, 7); g.fill();
    g.fillStyle = "rgba(255,244,220,0.12)";
    g.beginPath(); g.ellipse(x - r * 0.3, y - r * 0.35, r * 0.5, r * 0.4, 0, 0, 7); g.fill();
  }
  return { albedo: c, bump: 1.7 };
}

function makeGlass(): Layers {
  const { c, g } = cvs(ALBEDO);
  const grad = g.createLinearGradient(0, 0, ALBEDO, ALBEDO);
  grad.addColorStop(0, "#f5fbff");
  grad.addColorStop(0.45, "#bfd6e8");
  grad.addColorStop(1, "#ffffff");
  g.fillStyle = grad; g.fillRect(0, 0, ALBEDO, ALBEDO);
  g.strokeStyle = "rgba(255,255,255,0.55)"; g.lineWidth = 8;
  for (let x = -160; x < 640; x += 116) { g.beginPath(); g.moveTo(x, ALBEDO); g.lineTo(x + 240, 0); g.stroke(); }
  g.strokeStyle = "rgba(40,80,110,0.18)"; g.lineWidth = 4;
  g.strokeRect(4, 4, ALBEDO - 8, ALBEDO - 8);
  return { albedo: c, bump: 0 }; // glass is smooth on purpose
}

/** BARK — the tree trunks and branches.
 *
 *  The natural decor (trees/bushes/rocks/kelp) was the last untextured family
 *  in the game: flat-shaded vertex-coloured primitives, which is exactly the
 *  "toy" read the graphics mission is chasing out. Bark is the one of the three
 *  that needs a DEEP height field — a trunk's silhouette is round, so all of its
 *  form has to come from the normal map, and shallow bark reads as a painted
 *  dowel. Ridges run along V because every trunk is UV'd by `worldProjectUV`,
 *  which puts world +Y on V for a side-facing vertex. */
function makeBark(): Layers {
  const { c, g } = cvs(ALBEDO);
  const { c: hc, g: hg } = cvs(ALBEDO);
  g.fillStyle = "#c2ab92"; g.fillRect(0, 0, ALBEDO, ALBEDO);
  hg.fillStyle = "#8a8a8a"; hg.fillRect(0, 0, ALBEDO, ALBEDO);
  // vertical ridge columns of random width, each one a raised plate with a
  // deep wandering groove down its edge. The wander is what stops it reading
  // as corduroy.
  for (let x = 0; x < ALBEDO;) {
    const w = 8 + Math.random() * 26;
    const tone = 168 + Math.random() * 62;
    const hi = 150 + Math.random() * 85;
    let off = 0;
    for (let y = 0; y < ALBEDO; y += 4) {
      off += (Math.random() - 0.5) * 1.6;
      off = Math.max(-5, Math.min(5, off));
      g.fillStyle = `rgb(${tone},${tone * 0.86 | 0},${tone * 0.7 | 0})`;
      g.fillRect(x + off, y, w, 5);
      hg.fillStyle = `rgb(${hi},${hi},${hi})`;
      hg.fillRect(x + off, y, w, 5);
      // the groove: darker and much lower than the plate beside it
      g.fillStyle = "rgba(46,30,18,0.62)";
      g.fillRect(x + off + w - 3, y, 4, 5);
      hg.fillStyle = "rgb(26,26,26)";
      hg.fillRect(x + off + w - 3, y, 4, 5);
    }
    x += w;
  }
  noise(g, 42000, 0.11);
  // horizontal lenticel scars — short pale dashes across the ridges
  for (let i = 0; i < 260; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, w = 5 + Math.random() * 16;
    g.fillStyle = `rgba(228,214,186,${0.1 + Math.random() * 0.16})`;
    g.fillRect(x, y, w, 1 + Math.random() * 2);
  }
  // knots: concentric rings that break the vertical grain
  for (let i = 0; i < 5; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO;
    const rot = Math.random() * Math.PI;
    for (let k = 6; k > 0; k--) {
      g.strokeStyle = `rgba(58,36,20,${0.1 + k * 0.045})`; g.lineWidth = 2;
      hg.strokeStyle = `rgb(${40 + k * 16},${40 + k * 16},${40 + k * 16})`; hg.lineWidth = 3;
      g.beginPath(); g.ellipse(x, y, 4 + k * 3.4, 3 + k * 2.2, rot, 0, Math.PI * 2); g.stroke();
      hg.beginPath(); hg.ellipse(x, y, 4 + k * 3.4, 3 + k * 2.2, rot, 0, Math.PI * 2); hg.stroke();
    }
  }
  // moss / lichen crust — greenish, and MATTE, which is the roughness contrast
  const rc = roughFrom(c, 0.68, 1.0);
  const rg = rc.getContext("2d", { willReadFrequently: true })!;
  for (let i = 0; i < 40; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 8 + Math.random() * 30;
    g.fillStyle = `rgba(96,116,64,${0.1 + Math.random() * 0.18})`;
    g.beginPath(); g.ellipse(x, y, r, r * (0.5 + Math.random()), Math.random() * 3.14, 0, 7); g.fill();
    rg.fillStyle = "rgba(255,255,255,0.5)"; // fully matte where moss grows
    rg.beginPath();
    rg.ellipse(x * (ROUGH / ALBEDO), y * (ROUGH / ALBEDO), r * (ROUGH / ALBEDO), r * (ROUGH / ALBEDO) * 0.8, 0, 0, 7);
    rg.fill();
  }
  return { albedo: c, height: hc, rough: rc, bump: 3.4 };
}

/** CANOPY — tree crowns and bushes.
 *
 *  A leaf mass is not a mottle: it is thousands of overlapping BLADES with dark
 *  air gaps punched through and a few catching a specular highlight. The gaps
 *  matter most — without them a crown is a green ball, and a green ball is the
 *  toy silhouette. Distinct from `mcFoliage`, which is deliberately voxel-coarse
 *  for imported Minecraft leaf blocks.
 *
 *  The base tone is BRIGHT (`#cfe0b4`, mean ~0.83) on purpose. Every consumer
 *  multiplies this by a per-vertex green in the 0.25–0.4 range, and the first
 *  build used a mid-green base — the product came out visibly DARKER than the
 *  untextured crown it replaced, i.e. the texture was subtracting light instead
 *  of adding detail. A detail texture that rides on top of a vertex colour has
 *  to sit near white; the contrast has to come from the leaf-to-gap RANGE, not
 *  from the mean. Caught in the first capture of the vegetation pass, row 4. */
function makeCanopy(): Layers {
  const { c, g } = cvs(ALBEDO);
  const { c: hc, g: hg } = cvs(ALBEDO);
  g.fillStyle = "#cfe0b4"; g.fillRect(0, 0, ALBEDO, ALBEDO);
  hg.fillStyle = "#606060"; hg.fillRect(0, 0, ALBEDO, ALBEDO);
  // dark air gaps FIRST, so leaves layer on top of them and a few survive.
  // A canopy's realism lives in its DEPTH: without deep voids between the leaf
  // clumps the mass flattens into one green sheet (the "toy ball" read). Two
  // passes — broad clump shadows, then a handful of near-black inner voids that
  // punch real holes the sun can't reach — give the crown layered depth that the
  // finer canopy geometry now catches.
  for (let i = 0; i < 120; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 10 + Math.random() * 40;
    g.fillStyle = `rgba(58,84,50,${0.22 + Math.random() * 0.3})`;
    g.beginPath(); g.ellipse(x, y, r, r * (0.4 + Math.random() * 0.9), Math.random() * 3.14, 0, 7); g.fill();
    hg.fillStyle = "rgba(0,0,0,0.42)";
    hg.beginPath(); hg.ellipse(x, y, r, r * 0.7, 0, 0, 7); hg.fill();
  }
  for (let i = 0; i < 34; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 5 + Math.random() * 16;
    g.fillStyle = `rgba(30,48,28,${0.3 + Math.random() * 0.34})`;
    g.beginPath(); g.ellipse(x, y, r, r * (0.5 + Math.random() * 0.7), Math.random() * 3.14, 0, 7); g.fill();
    hg.fillStyle = "rgba(0,0,0,0.6)";
    hg.beginPath(); hg.ellipse(x, y, r * 0.8, r * 0.55, 0, 0, 7); hg.fill();
  }
  // three passes of individual leaves, coarse to fine, each with a midrib
  for (const [n, rmin, rmax, a] of [[420, 14, 30, 0.38], [900, 7, 16, 0.42], [1500, 3, 8, 0.4]] as const) {
    for (let i = 0; i < n; i++) {
      const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO;
      const r = rmin + Math.random() * (rmax - rmin), rot = Math.random() * Math.PI * 2;
      const l = 0.22 + Math.random() * 0.5; // per-leaf lightness
      g.fillStyle = `rgba(${(132 + l * 118) | 0},${(158 + l * 96) | 0},${(104 + l * 96) | 0},${a})`;
      g.beginPath(); g.ellipse(x, y, r, r * 0.42, rot, 0, Math.PI * 2); g.fill();
      const hv = (100 + l * 120) | 0;
      hg.fillStyle = `rgba(${hv},${hv},${hv},0.55)`;
      hg.beginPath(); hg.ellipse(x, y, r, r * 0.42, rot, 0, Math.PI * 2); hg.fill();
      if (r > 8) { // midrib — the vein that makes a blob read as a leaf
        g.strokeStyle = `rgba(${(96 + l * 80) | 0},${(120 + l * 80) | 0},${(78 + l * 60) | 0},0.45)`;
        g.lineWidth = 1;
        g.beginPath();
        g.moveTo(x - Math.cos(rot) * r, y - Math.sin(rot) * r);
        g.lineTo(x + Math.cos(rot) * r, y + Math.sin(rot) * r);
        g.stroke();
      }
    }
  }
  noise(g, 30000, 0.08);
  // sun-caught rim edges: bright, and GLOSSIER than the mass (waxy cuticle)
  const rc = roughFrom(c, 0.55, 1.0);
  const rg = rc.getContext("2d", { willReadFrequently: true })!;
  for (let i = 0; i < 420; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO;
    const r = 5 + Math.random() * 12, rot = Math.random() * Math.PI * 2;
    g.strokeStyle = `rgba(246,255,214,${0.2 + Math.random() * 0.26})`; g.lineWidth = 1.2;
    g.beginPath(); g.ellipse(x, y, r, r * 0.42, rot, 0, Math.PI); g.stroke();
    rg.fillStyle = "rgba(0,0,0,0.22)";
    rg.beginPath();
    rg.ellipse(x * (ROUGH / ALBEDO), y * (ROUGH / ALBEDO), r * (ROUGH / ALBEDO) * 1.2, r * (ROUGH / ALBEDO) * 0.6, rot, 0, 7);
    rg.fill();
  }
  return { albedo: c, height: hc, rough: rc, bump: 2.9 };
}

/** NEEDLE — conifer / pine foliage. The broadleaf `canopy` reads wrong on a
 *  spruce: a pine mass is not lobed leaves but a felt of thousands of short
 *  NEEDLES hanging off drooping sprays, darker and bluer than a deciduous
 *  crown, with hard black voids where the light doesn't reach between the
 *  branch tiers. Same rule as `canopy`: the base sits near white (`#c9d9b8`)
 *  because every consumer multiplies by a dark blue-green vertex/material tint,
 *  so the contrast has to come from the needle-to-void RANGE, not the mean. */
function makeNeedle(): Layers {
  const { c, g } = cvs(ALBEDO);
  const { c: hc, g: hg } = cvs(ALBEDO);
  g.fillStyle = "#c9d9b8"; g.fillRect(0, 0, ALBEDO, ALBEDO);
  hg.fillStyle = "#585858"; hg.fillRect(0, 0, ALBEDO, ALBEDO);
  // deep shadow voids between the sprays, punched first so needles sit on top
  for (let i = 0; i < 120; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 8 + Math.random() * 34;
    g.fillStyle = `rgba(52,74,52,${0.22 + Math.random() * 0.3})`;
    g.beginPath(); g.ellipse(x, y, r, r * (0.35 + Math.random() * 0.7), Math.random() * 3.14, 0, 7); g.fill();
    hg.fillStyle = "rgba(0,0,0,0.4)";
    hg.beginPath(); hg.ellipse(x, y, r, r * 0.6, 0, 0, 7); hg.fill();
  }
  // drooping sprays: a spine with short needles combed off both sides. Coarse
  // to fine so the mass has depth rather than one blade size.
  for (const [n, len, a] of [[260, 26, 0.4], [520, 15, 0.44]] as const) {
    for (let i = 0; i < n; i++) {
      const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO;
      const ang = Math.random() * Math.PI * 2;
      const dx = Math.cos(ang), dy = Math.sin(ang);
      const l = 0.2 + Math.random() * 0.55; // per-spray lightness
      const col = `rgba(${(96 + l * 92) | 0},${(128 + l * 92) | 0},${(78 + l * 74) | 0},${a})`;
      const hv = (86 + l * 120) | 0;
      // the spine, then needles fanning off it
      const px = -dx * len * (0.4 + Math.random() * 0.6);
      const needles = 5 + (Math.random() * 5 | 0);
      for (let k = 0; k <= needles; k++) {
        const t = k / needles;
        const sx = x + px + dx * len * t, sy = y + (-dy * len * 0.5) + dy * len * t + t * 2;
        const nl = 3 + Math.random() * 5; // needle length, drooping down
        const side = (k % 2 ? 1 : -1) * (0.6 + Math.random() * 0.5);
        g.strokeStyle = col; g.lineWidth = 1;
        g.beginPath(); g.moveTo(sx, sy); g.lineTo(sx + side * nl * 0.7, sy + nl); g.stroke();
        hg.strokeStyle = `rgba(${hv},${hv},${hv},0.5)`; hg.lineWidth = 1.4;
        hg.beginPath(); hg.moveTo(sx, sy); hg.lineTo(sx + side * nl * 0.7, sy + nl); hg.stroke();
      }
    }
  }
  noise(g, 26000, 0.07);
  // a few sun-caught needle tips: bright and slightly glossier (waxy)
  const rc = roughFrom(c, 0.62, 1.0);
  const rg = rc.getContext("2d", { willReadFrequently: true })!;
  for (let i = 0; i < 300; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, l = 3 + Math.random() * 6;
    g.strokeStyle = `rgba(214,236,180,${0.16 + Math.random() * 0.22})`; g.lineWidth = 1;
    g.beginPath(); g.moveTo(x, y); g.lineTo(x + (Math.random() - 0.5) * 3, y + l); g.stroke();
    rg.fillStyle = "rgba(0,0,0,0.2)";
    rg.beginPath();
    rg.ellipse(x * (ROUGH / ALBEDO), y * (ROUGH / ALBEDO), (ROUGH / ALBEDO) * 3, (ROUGH / ALBEDO) * 5, 0, 0, 7);
    rg.fill();
  }
  return { albedo: c, height: hc, rough: rc, bump: 2.0 };
}

/** HILLSIDE — the distant forested mountains that ring every day map. Seen only
 *  at range and through haze, so the read is a COARSE tree-cover mottle (clumps
 *  of canopy, not individual leaves) broken by pale rock scree and shadowed
 *  gullies — the three things that separate a real wooded slope from a flat
 *  green cone. Near-white base like the other foliage families (`makeCanopy`):
 *  every hill multiplies this by a per-ELEVATION vertex tint (forest at the
 *  foot, bare rock and snow at the ridge), so the contrast comes from the
 *  RANGE, not the mean. */
function makeHillside(): Layers {
  const { c, g } = cvs(ALBEDO);
  const { c: hc, g: hg } = cvs(ALBEDO);
  g.fillStyle = "#d9e0cc"; g.fillRect(0, 0, ALBEDO, ALBEDO);
  hg.fillStyle = "#7a7a7a"; hg.fillRect(0, 0, ALBEDO, ALBEDO);
  noise(g, 42000, 0.12);
  // forest-cover clumps: soft blotches, darker in the hollows, a lit crown on
  // the sun side — a whole stand of trees reduced to one mottle at this range,
  // with matching height-field relief so the normal map ripples like tree cover
  for (let i = 0; i < 520; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO;
    const r = 5 + Math.random() * 24;
    const dark = Math.random() < 0.5;
    g.fillStyle = dark
      ? `rgba(70,92,58,${0.14 + Math.random() * 0.2})`
      : `rgba(150,176,120,${0.12 + Math.random() * 0.16})`;
    g.beginPath(); g.ellipse(x, y, r, r * (0.6 + Math.random() * 0.5), Math.random() * 3.14, 0, 7); g.fill();
    const hv = dark ? 44 : 150;
    hg.fillStyle = `rgba(${hv},${hv},${hv},0.4)`;
    hg.beginPath(); hg.ellipse(x, y, r, r * 0.7, 0, 0, 7); hg.fill();
  }
  // SEDIMENTARY STRATA — gently tilted rock bands, exposed where the mountain
  // is bare stone. A single mottle reads as felt; layered strata reads as
  // GEOLOGY, and it is what stops a distant peak from looking like a green cone
  // painted grey. Baked into the height field too so the sun rakes across the
  // bedding planes on the reshaped ridges.
  for (let i = 0; i < 26; i++) {
    const y = Math.random() * ALBEDO;
    const th = 2 + Math.random() * 7, tilt = (Math.random() - 0.5) * 0.18;
    const shade = 120 + Math.floor(Math.random() * 70);
    g.save(); g.translate(0, y); g.rotate(tilt);
    g.fillStyle = `rgba(${shade - 26},${shade - 22},${shade - 30},${0.1 + Math.random() * 0.12})`;
    g.fillRect(-40, -th / 2, ALBEDO + 80, th);
    g.restore();
    hg.save(); hg.translate(0, y); hg.rotate(tilt);
    const hv = 90 + Math.floor(Math.random() * 90);
    hg.fillStyle = `rgba(${hv},${hv},${hv},0.28)`;
    hg.fillRect(-40, -th / 2, ALBEDO + 80, th);
    hg.restore();
  }
  // ROCK SCREE — pale diagonal bands where the slope is too steep for trees
  for (let i = 0; i < 14; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO;
    const w = 24 + Math.random() * 70, rot = (Math.random() - 0.5) * 1.2;
    g.save(); g.translate(x, y); g.rotate(rot);
    const grd = g.createLinearGradient(0, -w, 0, w);
    grd.addColorStop(0, "rgba(188,182,168,0)");
    grd.addColorStop(0.5, "rgba(188,182,168,0.28)");
    grd.addColorStop(1, "rgba(188,182,168,0)");
    g.fillStyle = grd; g.fillRect(-w, -w, w * 2, w * 2);
    g.restore();
  }
  // shadowed GULLIES — dark branching lines running downslope, cut into the
  // height field as well so they catch a shadow at a grazing sun
  g.lineCap = "round"; hg.lineCap = "round";
  for (let i = 0; i < 20; i++) {
    let x = Math.random() * ALBEDO, y = Math.random() * ALBEDO * 0.4;
    const pts: [number, number][] = [[x, y]];
    for (let s = 0; s < 6; s++) { x += (Math.random() - 0.5) * 40; y += 40 + Math.random() * 40; pts.push([x, y]); }
    const lw = 1 + Math.random() * 3;
    g.strokeStyle = `rgba(46,58,44,${0.1 + Math.random() * 0.12})`; g.lineWidth = lw;
    g.beginPath(); g.moveTo(pts[0][0], pts[0][1]); for (const p of pts) g.lineTo(p[0], p[1]); g.stroke();
    hg.strokeStyle = "rgba(0,0,0,0.3)"; hg.lineWidth = lw + 1;
    hg.beginPath(); hg.moveTo(pts[0][0], pts[0][1]); for (const p of pts) hg.lineTo(p[0], p[1]); hg.stroke();
  }
  return { albedo: c, height: hc, bump: 2.5 };
}

/** BOULDER — the scattered rocks, on every map and both themes.
 *
 *  Granite, not "grey". The read comes from three separate frequencies: mineral
 *  speckle (quartz bright, mica near-black), fracture lines that cross the
 *  whole tile, and lichen crusts that move ROUGHNESS far more than albedo. The
 *  lichen is doing the same job the puddles do on `paving` — one material that
 *  behaves like two under a moving light. */
/** WOVEN CLOTH — banners, flags, awnings, tent canvas, sails.
 *
 *  The whole family is one idea: a fabric is *threads*, and a thread is a
 *  cylinder. Everything below feeds the HEIGHT field first and the albedo
 *  second, because what makes canvas read as canvas at 3 m is the weave
 *  catching the sun along one axis and shading along the other — not its
 *  colour. The albedo base sits near white (`#e6e2d8`, mean ~0.88) on purpose:
 *  every consumer multiplies this by a strong banner colour, and the canopy
 *  lesson from the vegetation pass applies verbatim — a detail texture that
 *  rides on a tint has to sit near white, the contrast comes from the RANGE. */
function makeCloth(): Layers {
  const { c, g } = cvs(ALBEDO);
  const { c: hc, g: hg } = cvs(ALBEDO);
  g.fillStyle = "#e6e2d8"; g.fillRect(0, 0, ALBEDO, ALBEDO);
  hg.fillStyle = "#808080"; hg.fillRect(0, 0, ALBEDO, ALBEDO);

  // --- THE WEAVE. Warp (vertical) and weft (horizontal) threads on an 8 px
  // pitch, each drawn as a 3-stop gradient across its own width so it reads as
  // a round thread rather than a stripe. Weft rides OVER warp on alternating
  // columns (the `over` parity), which is what makes a plain weave look woven
  // instead of gridded.
  const PITCH = 8;
  const thread = (
    ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number,
    lo: string, mid: string, hi: string,
  ): void => {
    const grad = w > h ? ctx.createLinearGradient(0, y, 0, y + h) : ctx.createLinearGradient(x, 0, x + w, 0);
    grad.addColorStop(0, lo); grad.addColorStop(0.42, hi); grad.addColorStop(1, mid);
    ctx.fillStyle = grad;
    ctx.fillRect(x, y, w, h);
  };
  for (let i = 0; i * PITCH < ALBEDO; i++) {
    const x = i * PITCH + (Math.random() - 0.5) * 0.8; // hand-loom jitter
    thread(g, x, 0, PITCH - 1.4, ALBEDO, "rgba(150,144,132,0.30)", "rgba(196,190,176,0.22)", "rgba(255,253,246,0.34)");
    thread(hg, x, 0, PITCH - 1.4, ALBEDO, "rgba(74,74,74,0.85)", "rgba(120,120,120,0.7)", "rgba(206,206,206,0.9)");
  }
  for (let j = 0; j * PITCH < ALBEDO; j++) {
    const y = j * PITCH + (Math.random() - 0.5) * 0.8;
    for (let i = 0; i * PITCH < ALBEDO; i++) {
      if ((i + j) % 2 === 1) continue; // this warp passes OVER the weft here
      const x = i * PITCH;
      thread(g, x, y, PITCH, PITCH - 1.4, "rgba(148,142,130,0.34)", "rgba(198,192,178,0.24)", "rgba(255,253,246,0.36)");
      thread(hg, x, y, PITCH, PITCH - 1.4, "rgba(70,70,70,0.9)", "rgba(124,124,124,0.75)", "rgba(212,212,212,0.95)");
    }
  }

  // --- SLUBS: fat irregular thread segments. Real woven cloth has them and
  // they break the perfect lattice, which is the giveaway of a fake weave.
  for (let i = 0; i < 260; i++) {
    const vertical = Math.random() < 0.5;
    const x = Math.floor(Math.random() * (ALBEDO / PITCH)) * PITCH;
    const y = Math.floor(Math.random() * (ALBEDO / PITCH)) * PITCH;
    const len = PITCH * (2 + Math.floor(Math.random() * 5));
    const a = 0.1 + Math.random() * 0.16;
    g.fillStyle = `rgba(255,252,244,${a})`;
    hg.fillStyle = `rgba(232,232,232,${a + 0.25})`;
    const w = vertical ? PITCH - 1 : len, h = vertical ? len : PITCH - 1;
    g.fillRect(x, y, w, h);
    hg.fillRect(x, y, w, h);
  }

  // --- HEM STITCHING: two runs of dashes near two opposite edges. A banner is
  // a hemmed rectangle and the stitch line is the cue that it was SEWN.
  for (const [hx, hy, horiz] of [[0, 14, true], [0, ALBEDO - 14, true], [16, 0, false], [ALBEDO - 16, 0, false]] as const) {
    for (let t = 0; t < ALBEDO; t += 11) {
      const w = horiz ? 6 : 2.4, h = horiz ? 2.4 : 6;
      const x = horiz ? t : hx, y = horiz ? hy : t;
      g.fillStyle = "rgba(112,104,90,0.34)"; g.fillRect(x, y, w, h);
      hg.fillStyle = "rgba(214,214,214,0.8)"; hg.fillRect(x, y, w, h);
      hg.fillStyle = "rgba(46,46,46,0.5)"; hg.fillRect(x, y + h, w, 1.4);
    }
  }

  // --- WEAR. Sun fade (broad, bright, low-contrast) and grime (low, dirty).
  for (let i = 0; i < 42; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 30 + Math.random() * 110;
    const fade = Math.random() < 0.55;
    const rg2 = g.createRadialGradient(x, y, 0, x, y, r);
    rg2.addColorStop(0, fade ? "rgba(255,255,250,0.20)" : "rgba(92,84,68,0.17)");
    rg2.addColorStop(1, "rgba(0,0,0,0)");
    g.fillStyle = rg2;
    g.beginPath(); g.ellipse(x, y, r, r * (0.5 + Math.random() * 0.8), Math.random() * 3.14, 0, 7); g.fill();
  }
  noise(g, 46000, 0.10);

  // --- ROUGHNESS. Cloth is matte everywhere (a narrow band), with two
  // exceptions worth painting: grime is DEAD flat, and the raised thread crowns
  // where a banner has been handled take a faint sheen.
  const rc = roughFrom(c, 0.78, 1.0);
  const rg = rc.getContext("2d", { willReadFrequently: true })!;
  const k = ROUGH / ALBEDO;
  for (let i = 0; i < 30; i++) { // polished/handled patches — glossier
    const x = Math.random() * ROUGH, y = Math.random() * ROUGH, r = 4 + Math.random() * 16;
    rg.fillStyle = `rgba(0,0,0,${0.14 + Math.random() * 0.18})`;
    rg.beginPath(); rg.ellipse(x, y, r, r * (0.5 + Math.random() * 0.8), Math.random() * 3.14, 0, 7); rg.fill();
  }
  // --- FRAYS + PINHOLES: tiny dark nicks along the weave. Cheap, and the eye
  // reads them as age instantly.
  for (let i = 0; i < 190; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO;
    const r = 0.9 + Math.random() * 2.6;
    g.fillStyle = `rgba(58,52,42,${0.16 + Math.random() * 0.3})`;
    g.beginPath(); g.arc(x, y, r, 0, 7); g.fill();
    hg.fillStyle = "rgba(24,24,24,0.7)";
    hg.beginPath(); hg.arc(x, y, r, 0, 7); hg.fill();
    rg.fillStyle = "rgba(255,255,255,0.5)";
    rg.beginPath(); rg.arc(x * k, y * k, Math.max(0.6, r * k * 2), 0, 7); rg.fill();
  }
  return { albedo: c, height: hc, rough: rc, bump: 1.9 };
}

function makeBoulder(): Layers {
  const { c, g } = cvs(ALBEDO);
  g.fillStyle = "#b9b4a8"; g.fillRect(0, 0, ALBEDO, ALBEDO);
  // broad tonal blotches — no rock is one grey
  for (let i = 0; i < 70; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 20 + Math.random() * 70;
    g.fillStyle = Math.random() < 0.5 ? "rgba(92,88,80,0.13)" : "rgba(214,208,194,0.13)";
    g.beginPath(); g.ellipse(x, y, r, r * (0.6 + Math.random() * 0.7), Math.random() * 3.14, 0, 7); g.fill();
  }
  noise(g, 90000, 0.2);
  // mineral grain: bright quartz + near-black mica, at two crystal sizes
  for (const [n, rmin, rmax] of [[2600, 1.2, 3.2], [700, 3, 7]] as const) {
    for (let i = 0; i < n; i++) {
      const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO;
      const r = rmin + Math.random() * (rmax - rmin);
      g.fillStyle = Math.random() < 0.55
        ? `rgba(246,242,230,${0.14 + Math.random() * 0.24})`
        : `rgba(34,30,28,${0.14 + Math.random() * 0.26})`;
      g.beginPath(); g.ellipse(x, y, r, r * (0.5 + Math.random() * 0.8), Math.random() * 3.14, 0, 7); g.fill();
    }
  }
  // fractures: long shallow-angle cracks, each with a lit lower lip
  for (let i = 0; i < 22; i++) {
    let x = Math.random() * ALBEDO, y = Math.random() * ALBEDO;
    const a = Math.random() * Math.PI * 2;
    const pts: [number, number][] = [[x, y]];
    for (let k = 0; k < 14; k++) {
      x += Math.cos(a + (Math.random() - 0.5) * 1.1) * (14 + Math.random() * 22);
      y += Math.sin(a + (Math.random() - 0.5) * 1.1) * (14 + Math.random() * 22);
      pts.push([x, y]);
    }
    for (const [dx, dy, style, lw] of [[0, 0, "rgba(38,34,30,0.5)", 2.2], [0.5, 1.6, "rgba(232,228,214,0.16)", 1.4]] as const) {
      g.strokeStyle = style; g.lineWidth = lw;
      g.beginPath();
      for (const [px, py] of pts) g.lineTo(px + dx, py + dy);
      g.stroke();
    }
  }
  // chipped facets — angular bright planes where the rock has spalled
  for (let i = 0; i < 26; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 8 + Math.random() * 26;
    g.fillStyle = `rgba(228,222,206,${0.08 + Math.random() * 0.12})`;
    g.beginPath();
    for (let k = 0; k < 5; k++) {
      const a = (k / 5) * Math.PI * 2;
      g.lineTo(x + Math.cos(a) * r * (0.6 + Math.random() * 0.6), y + Math.sin(a) * r * (0.6 + Math.random() * 0.6));
    }
    g.closePath(); g.fill();
  }
  // lichen crust: pale sage rosettes, and DEAD matte where they grow
  const rc = roughFrom(c, 0.42, 0.92);
  const rg = rc.getContext("2d", { willReadFrequently: true })!;
  for (let i = 0; i < 70; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 6 + Math.random() * 26;
    const sage = Math.random() < 0.6;
    g.fillStyle = sage ? `rgba(158,176,132,${0.1 + Math.random() * 0.2})` : `rgba(196,178,120,${0.08 + Math.random() * 0.16})`;
    g.beginPath();
    for (let k = 0; k < 9; k++) {
      const a = (k / 9) * Math.PI * 2;
      const rr = r * (0.55 + Math.random() * 0.7);
      g.lineTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr);
    }
    g.closePath(); g.fill();
    rg.fillStyle = "rgba(255,255,255,0.55)";
    rg.beginPath();
    rg.arc(x * (ROUGH / ALBEDO), y * (ROUGH / ALBEDO), r * (ROUGH / ALBEDO), 0, 7);
    rg.fill();
  }
  return { albedo: c, rough: rc, bump: 2.6 };
}

/**
 * ROOFING FELT (2026-08-01, `graphics` — the ROOFLINE pass).
 *
 * Every roof in this game wore the same texture as the wall under it, because
 * `worldProjectUV` projects per face and a building's family material is one
 * material for all six. On a `building` box that means the TOP face was painted
 * with the office-window `facade` tile — a grid of lit windows lying flat,
 * looking up at the sky. From any elevated position (and this game has
 * jetpacks, ziplines, helicopters and three rooftop maps) that is a large part
 * of the screen wearing an obviously wrong material.
 *
 * A flat commercial roof is a BUILT-UP membrane: rolls of bitumen felt lapped
 * side by side, the laps running one way only, mopped down with tar that
 * squeezes out along the seam, ballasted with pea gravel, and patched where it
 * has failed.
 *
 * The direction matters: the laps are HORIZONTAL in UV space and nothing
 * crosses them, which is what makes a roof read as rolled material rather than
 * as tiles.
 */
function makeRoofing(): Layers {
  const { c, g } = cvs(ALBEDO);
  const { c: hc, g: hg } = cvs(ALBEDO);
  g.fillStyle = "#dcd8d2"; g.fillRect(0, 0, ALBEDO, ALBEDO);
  hg.fillStyle = "#7a7a7a"; hg.fillRect(0, 0, ALBEDO, ALBEDO);

  // --- ROLL LAPS. Four rolls per tile (128 px each). Each roll gets its own
  // faint tone (bitumen batches never match) and the LAP EDGE gets a tar bead:
  // a dark line with a raised lip, which is the one piece of relief a flat roof
  // has and therefore the only thing that catches the low sun.
  for (let r = 0; r < 4; r++) {
    const y = r * 128;
    const tone = 0.5 + ((r * 37) % 11) / 22; // deterministic-ish per roll
    g.fillStyle = `rgba(${(150 + tone * 40) | 0},${(146 + tone * 38) | 0},${(140 + tone * 36) | 0},0.16)`;
    g.fillRect(0, y, ALBEDO, 128);
    // tar squeeze-out along the lap, then the raised lip above it
    g.fillStyle = "rgba(38,34,30,0.34)"; g.fillRect(0, y, ALBEDO, 5);
    g.fillStyle = "rgba(255,252,244,0.10)"; g.fillRect(0, y + 5, ALBEDO, 2);
    hg.fillStyle = "rgba(214,214,214,0.9)"; hg.fillRect(0, y - 3, ALBEDO, 8);
    hg.fillStyle = "rgba(52,52,52,0.6)"; hg.fillRect(0, y + 5, ALBEDO, 3);
    // the bead wanders — a straight-edged lap reads as a printed stripe
    for (let x = 0; x < ALBEDO; x += 9) {
      const w = 9 + Math.random() * 8, dy = (Math.random() - 0.5) * 3.4;
      g.fillStyle = `rgba(30,27,24,${0.1 + Math.random() * 0.2})`;
      g.fillRect(x, y + dy, w, 4 + Math.random() * 3);
      hg.fillStyle = `rgba(230,230,230,${0.3 + Math.random() * 0.3})`;
      hg.fillRect(x, y + dy - 1, w, 4);
    }
  }

  // --- PEA GRAVEL BALLAST. Thousands of tiny stones half-sunk in the bitumen:
  // a lit crown, a dark shadow side, and a real bump in the height field. This
  // is the frequency that stops the roof reading as a painted plane at 3 m.
  for (let i = 0; i < 5200; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO;
    const r = 1.1 + Math.random() * 3.4, rot = Math.random() * Math.PI;
    const v = (120 + Math.random() * 120) | 0;
    g.fillStyle = `rgba(${v},${v - 4},${(v - 12) | 0},${0.12 + Math.random() * 0.22})`;
    g.beginPath(); g.ellipse(x, y, r, r * (0.6 + Math.random() * 0.5), rot, 0, 7); g.fill();
    g.fillStyle = "rgba(255,255,250,0.16)";
    g.beginPath(); g.ellipse(x - r * 0.3, y - r * 0.32, r * 0.44, r * 0.3, rot, 0, 7); g.fill();
    const dome = hg.createRadialGradient(x, y, 0, x, y, r * 1.5);
    dome.addColorStop(0, "rgba(240,240,240,0.85)"); dome.addColorStop(1, "rgba(122,122,122,0)");
    hg.fillStyle = dome; hg.beginPath(); hg.arc(x, y, r * 1.5, 0, 7); hg.fill();
  }
  noise(g, 60000, 0.12);

  // --- PATCH REPAIRS. A rectangle of fresh (darker, gravel-free) membrane with
  // a fat mastic bead all round it. Nothing says "this surface has a service
  // history" faster, and it breaks the roll rhythm the eye would otherwise lock
  // onto.
  for (let i = 0; i < 7; i++) {
    const pw = 40 + Math.random() * 120, ph = 30 + Math.random() * 90;
    const x = Math.random() * (ALBEDO - pw), y = Math.random() * (ALBEDO - ph);
    g.fillStyle = `rgba(48,44,40,${0.16 + Math.random() * 0.14})`;
    g.fillRect(x, y, pw, ph);
    g.strokeStyle = "rgba(24,21,18,0.4)"; g.lineWidth = 4;
    g.strokeRect(x, y, pw, ph);
    g.strokeStyle = "rgba(255,250,240,0.13)"; g.lineWidth = 1.4;
    g.strokeRect(x - 2.5, y - 2.5, pw + 5, ph + 5);
    hg.strokeStyle = "rgba(212,212,212,0.75)"; hg.lineWidth = 5;
    hg.strokeRect(x, y, pw, ph);
  }

  const rc = roughFrom(c, 0.72, 1.0);
  return { albedo: c, height: hc, rough: rc, bump: 2.8 };
}

/**
 * GALVANISED SHEET (2026-08-01, `graphics` — the ROOFLINE pass).
 *
 * The surface of every piece of roof plant: the AC housings, the water tank,
 * the vent stacks, the stair hut. `surface` (brushed panel + rivets) was the
 * closest existing family and it is wrong in the way that matters — plant is
 * CORRUGATED, and a corrugation is a shape the light rolls across, not a grain.
 *
 * So the height field is built first and the albedo is derived from it: a
 * sinusoidal rib profile on a 32 px pitch, running along V. On top of that go
 * the two things that date galvanised steel — the SPANGLE (the crystalline
 * zinc flower, only ever visible on real hot-dip sheet) and RUST BLEED running
 * DOWN from every fastener. The bleed is directional, which quietly tells the
 * eye which way is up on an object with no other cue.
 */
function makeGalv(): Layers {
  const { c, g } = cvs(ALBEDO);
  const { c: hc, g: hg } = cvs(ALBEDO);
  g.fillStyle = "#d8dce0"; g.fillRect(0, 0, ALBEDO, ALBEDO);
  hg.fillStyle = "#808080"; hg.fillRect(0, 0, ALBEDO, ALBEDO);

  // --- CORRUGATION. 16 ribs across the tile; the shading is a real cosine so
  // the crest highlight and the valley shadow line up with the normal map
  // instead of merely resembling it.
  const PITCH = ALBEDO / 16;
  for (let x = 0; x < ALBEDO; x++) {
    const p = (x % PITCH) / PITCH;
    const h = Math.cos(p * Math.PI * 2);         // +1 crest, -1 valley
    const lit = 0.5 + 0.5 * Math.cos((p - 0.12) * Math.PI * 2);
    g.fillStyle = `rgba(255,255,255,${Math.max(0, lit - 0.45) * 0.7})`;
    g.fillRect(x, 0, 1, ALBEDO);
    g.fillStyle = `rgba(28,34,44,${Math.max(0, 0.5 - lit) * 0.62})`;
    g.fillRect(x, 0, 1, ALBEDO);
    const v = (128 + h * 118) | 0;
    hg.fillStyle = `rgb(${v},${v},${v})`;
    hg.fillRect(x, 0, 1, ALBEDO);
  }

  // --- SPANGLE. Overlapping angular zinc crystals, bright-edged and faint. The
  // one detail nobody paints and everybody recognises.
  for (let i = 0; i < 260; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 8 + Math.random() * 30;
    const rot = Math.random() * Math.PI * 2, n = 5 + ((Math.random() * 3) | 0);
    g.strokeStyle = `rgba(255,255,255,${0.05 + Math.random() * 0.1})`; g.lineWidth = 1;
    g.fillStyle = `rgba(206,214,222,${0.04 + Math.random() * 0.07})`;
    g.beginPath();
    for (let s = 0; s <= n; s++) {
      const a = rot + (s / n) * Math.PI * 2;
      const rr = r * (0.6 + ((s * 7) % 5) / 9);
      g.lineTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr);
    }
    g.closePath(); g.fill(); g.stroke();
  }

  // --- FASTENERS + RUST BLEED. A screw every other rib crest, on two rows,
  // each with a washer shadow and a stain running down from it.
  const rc = roughFrom(c, 0.3, 0.72);
  const rg = rc.getContext("2d", { willReadFrequently: true })!;
  const k = ROUGH / ALBEDO;
  for (const row of [96, 352]) {
    for (let i = 0; i < 8; i++) {
      const x = i * PITCH * 2 + PITCH * 0.5, y = row + (Math.random() - 0.5) * 6;
      g.fillStyle = "rgba(22,26,32,0.42)"; g.beginPath(); g.arc(x + 1, y + 1.4, 4.4, 0, 7); g.fill();
      g.fillStyle = "rgba(246,248,252,0.5)"; g.beginPath(); g.arc(x - 0.6, y - 0.8, 2.8, 0, 7); g.fill();
      const dome = hg.createRadialGradient(x, y, 0, x, y, 6);
      dome.addColorStop(0, "rgba(255,255,255,0.9)"); dome.addColorStop(1, "rgba(128,128,128,0)");
      hg.fillStyle = dome; hg.beginPath(); hg.arc(x, y, 6, 0, 7); hg.fill();
      // the bleed: a narrowing rust tail, always downward (+y)
      const len = 20 + Math.random() * 90;
      const grd = g.createLinearGradient(x, y, x, y + len);
      grd.addColorStop(0, "rgba(146,74,32,0.34)");
      grd.addColorStop(0.35, "rgba(158,92,44,0.18)");
      grd.addColorStop(1, "rgba(150,90,50,0)");
      g.fillStyle = grd;
      g.beginPath();
      g.moveTo(x - 3.4, y); g.lineTo(x + 3.4, y);
      g.lineTo(x + 1.2, y + len); g.lineTo(x - 1.2, y + len);
      g.closePath(); g.fill();
      rg.fillStyle = "rgba(255,255,255,0.5)"; // rust is DEAD matte next to zinc
      rg.fillRect((x - 3) * k, y * k, 6 * k, len * k);
    }
  }

  // --- DENTS + WEATHERING. Plant lives outdoors and gets hit; a few soft
  // depressions and grimy patches keep the ribs from reading as extruded
  // plastic.
  for (let i = 0; i < 30; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 10 + Math.random() * 34;
    const grd = hg.createRadialGradient(x, y, 0, x, y, r);
    grd.addColorStop(0, "rgba(40,40,40,0.5)"); grd.addColorStop(1, "rgba(128,128,128,0)");
    hg.fillStyle = grd; hg.beginPath(); hg.arc(x, y, r, 0, 7); hg.fill();
    g.fillStyle = `rgba(96,102,112,${0.05 + Math.random() * 0.08})`;
    g.beginPath(); g.ellipse(x, y, r, r * 0.7, Math.random() * 3.14, 0, 7); g.fill();
    rg.fillStyle = `rgba(255,255,255,${0.14 + Math.random() * 0.2})`;
    rg.beginPath(); rg.ellipse(x * k, y * k, r * k, r * k * 0.7, 0, 0, 7); rg.fill();
  }
  noise(g, 34000, 0.09);
  return { albedo: c, height: hc, rough: rc, bump: 3.2 };
}

/**
 * LOUVRED GRILLE (2026-08-05, `graphics` — the ROOFLINE ROUND TWO pass).
 *
 * The roof plant shipped in the roofline pass wore ONE material for everything:
 * `galv` corrugated sheet on the tank, the stacks, the stair hut AND the
 * condenser units. Corrugation is right for a hut and a tank and wrong for a
 * condenser — the thing that makes an air-handling unit legible at forty metres
 * is that its flanks are OPEN: horizontal louvre blades over a punched
 * perforation, dark between the blades, bright on every blade lip. That
 * alternation is a much stronger cue than a rib, because the dark gaps read as
 * depth rather than as shading.
 *
 * Built height-first like `galv`: a per-row sawtooth blade profile drives both
 * the albedo shading and the height field, so the normal map's blade lip and
 * the albedo's blade lip are the same line rather than two lines that resemble
 * each other. On top go the punched holes (a real dip in the height field, so
 * they survive as relief when the tile is too far away to resolve), the sheet
 * FRAME that turns the tile into a panel, and the grime that always collects on
 * the underside of a blade.
 *
 * The blades run along V, i.e. HORIZONTAL in world space for any wall face that
 * `worldProjectUV` maps — which is the orientation a real condenser grille has
 * and the one the eye checks first.
 */
function makeLouvre(): Layers {
  const { c, g } = cvs(ALBEDO);
  const { c: hc, g: hg } = cvs(ALBEDO);
  g.fillStyle = "#b9bfc6"; g.fillRect(0, 0, ALBEDO, ALBEDO);
  hg.fillStyle = "#787878"; hg.fillRect(0, 0, ALBEDO, ALBEDO);

  // --- BLADES. 16 per tile. `p` runs 0 at the blade's top lip to 1 at the next
  // lip; the blade face occupies the first 74% (sloping down and away, so it
  // darkens toward its root) and the remainder is the OPEN GAP behind it, which
  // is the darkest value in the whole texture set on purpose.
  const BLADES = 16;
  const P = ALBEDO / BLADES;
  for (let y = 0; y < ALBEDO; y++) {
    const p = (y % P) / P;
    const open = p > 0.74;
    if (open) {
      // the gap: near-black, and a deep trough in the height field
      const t = (p - 0.74) / 0.26;
      g.fillStyle = `rgba(14,17,22,${0.62 + 0.24 * Math.sin(t * Math.PI)})`;
      g.fillRect(0, y, ALBEDO, 1);
      const v = (22 + 26 * (1 - Math.sin(t * Math.PI))) | 0;
      hg.fillStyle = `rgb(${v},${v},${v})`;
      hg.fillRect(0, y, ALBEDO, 1);
    } else {
      // the blade: a bright lip at p≈0 falling to a shadowed root at p≈0.74
      const f = p / 0.74;
      g.fillStyle = `rgba(255,255,255,${Math.max(0, 0.46 - f * 0.62)})`;
      g.fillRect(0, y, ALBEDO, 1);
      g.fillStyle = `rgba(24,30,40,${Math.max(0, f - 0.42) * 0.55})`;
      g.fillRect(0, y, ALBEDO, 1);
      const v = (214 - f * 118) | 0;
      hg.fillStyle = `rgb(${v},${v},${v})`;
      hg.fillRect(0, y, ALBEDO, 1);
    }
  }

  // --- PUNCHED PERFORATION. A staggered hole grid across the blade faces: each
  // hole is a dark bore with a bright burr on its upper rim (the side the press
  // pushed the metal out of) and a real pit in the height field. Skipped where
  // it would land in a gap — you cannot punch a hole through empty air.
  const HP = 14;
  for (let hy = 0; hy < ALBEDO; hy += HP) {
    const row = (hy / HP) | 0;
    const p = (hy % P) / P;
    if (p > 0.62) continue;
    for (let hx = (row & 1) ? HP / 2 : 0; hx < ALBEDO; hx += HP) {
      const r = 2.6;
      g.fillStyle = "rgba(16,19,25,0.5)";
      g.beginPath(); g.arc(hx, hy, r, 0, 7); g.fill();
      g.fillStyle = "rgba(255,255,255,0.18)";
      g.beginPath(); g.arc(hx, hy - r * 0.55, r * 0.62, 0, 7); g.fill();
      const pit = hg.createRadialGradient(hx, hy, 0, hx, hy, r * 1.8);
      pit.addColorStop(0, "rgba(20,20,20,0.85)"); pit.addColorStop(1, "rgba(128,128,128,0)");
      hg.fillStyle = pit; hg.beginPath(); hg.arc(hx, hy, r * 1.8, 0, 7); hg.fill();
    }
  }

  // --- THE PANEL FRAME. A condenser grille is a SHEET in a frame, not an
  // infinite field of blades: a raised border round the tile edge (drawn at
  // both wrap edges so it still tiles) plus corner fixings.
  const rc = roughFrom(c, 0.3, 0.8);
  const rg = rc.getContext("2d", { willReadFrequently: true })!;
  const k = ROUGH / ALBEDO;
  const FR = 9;
  for (const [x, y, w, h] of [
    [0, 0, ALBEDO, FR], [0, ALBEDO - FR, ALBEDO, FR],
    [0, 0, FR, ALBEDO], [ALBEDO - FR, 0, FR, ALBEDO],
  ] as [number, number, number, number][]) {
    g.fillStyle = "rgba(178,186,194,0.92)"; g.fillRect(x, y, w, h);
    hg.fillStyle = "rgba(238,238,238,0.95)"; hg.fillRect(x, y, w, h);
    rg.fillStyle = "rgba(255,255,255,0.25)"; rg.fillRect(x * k, y * k, w * k, h * k);
  }
  g.strokeStyle = "rgba(30,36,44,0.5)"; g.lineWidth = 2;
  g.strokeRect(FR, FR, ALBEDO - FR * 2, ALBEDO - FR * 2);
  for (const [fx, fy] of [[FR * 0.5, FR * 0.5], [ALBEDO - FR * 0.5, FR * 0.5],
    [FR * 0.5, ALBEDO - FR * 0.5], [ALBEDO - FR * 0.5, ALBEDO - FR * 0.5]]) {
    g.fillStyle = "rgba(28,34,42,0.45)"; g.beginPath(); g.arc(fx, fy, 3.6, 0, 7); g.fill();
    g.fillStyle = "rgba(250,252,255,0.5)"; g.beginPath(); g.arc(fx - 0.7, fy - 0.9, 2.1, 0, 7); g.fill();
  }

  // --- GRIME. Dirt is drawn INTO the machine by the fan, so it collects on the
  // blade roots and streaks downward from the gaps — the one asymmetry that
  // tells the eye which way is up on an object with no other cue.
  for (let i = 0; i < 120; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO;
    const len = 8 + Math.random() * 46;
    const grd = g.createLinearGradient(x, y, x, y + len);
    grd.addColorStop(0, "rgba(52,50,46,0.3)");
    grd.addColorStop(1, "rgba(60,58,52,0)");
    g.fillStyle = grd; g.fillRect(x - 2.5, y, 5, len);
    rg.fillStyle = "rgba(255,255,255,0.35)";
    rg.fillRect((x - 3) * k, y * k, 6 * k, len * k);
  }
  noise(g, 26000, 0.08);
  return { albedo: c, height: hc, rough: rc, bump: 3.6 };
}

/**
 * SIGNAGE (2026-08-05, `graphics`) — the twenty-first family, and the second
 * in this file to ship an EMISSIVE layer alongside its albedo (`facade` is the
 * other one).
 *
 * The game is called NEON PURGE and until now not one building in it said
 * anything. Every facade was wall, window, trim, wall. What tells you a street
 * is a street — at a hundred metres, at night, through fog — is SIGNAGE: a lit
 * box over a door and a blade hanging out over the pavement. It is also the
 * only kind of map detail that is legible at range *because* it emits its own
 * light instead of waiting for the sun.
 *
 * THIS TILE IS A 2x2 ATLAS, not a tiling pattern, and that is the one thing a
 * caller must know. Four independent 256 px sign faces, each with its own tube
 * colour; `mapDressing` hands the renderer a cell index per sign and the
 * renderer maps that sign's quad into that cell. A repeating tile was the
 * obvious build and it is wrong for this subject: a street where every sign is
 * the same sign is worse than a street with no signs, and four is enough that
 * the eye stops matching them up.
 *
 * Built glow-first, the same discipline `galv`/`louvre` used for height: the
 * TUBE PATH is drawn once as a list of closures and then replayed onto all four
 * layers, so the bright line in the emissive map, the pale glass in the albedo,
 * the ridge in the height field and the gloss in the roughness map are provably
 * the same curve. Draw them independently and a neon sign reads as a painted
 * sign with a glow decal slapped near it.
 *
 * Every cell is: a steel case (frame + bolts, the part that is still visible in
 * broad daylight when the tubes lose), a dark recessed face, a border tube
 * following the case, and three abstract glyphs. Abstract on purpose — legible
 * lettering would have to be a language, and at the 3-6 px per glyph this is
 * actually read at, stroke rhythm is the entire signal.
 */
const SIGN_HUES = ["#ff2f8e", "#2ff0ff", "#ffb636", "#7bff6a"];
function makeSignage(): { layers: Layers; glow: HTMLCanvasElement } {
  const { c: albedo, g: a } = cvs(ALBEDO);
  const { c: hc, g: hg } = cvs(ALBEDO);
  const { c: rc, g: rg } = cvs(ROUGH);
  const { c: glow, g } = cvs(ALBEDO);
  const S = ALBEDO / 2;            // one atlas cell
  const RS = ROUGH / ALBEDO;       // roughness is 1/4 scale

  g.fillStyle = "#000"; g.fillRect(0, 0, ALBEDO, ALBEDO); // unlit everywhere else
  /** abstract glyph strokes in a 0..1 box — segment lists, replayed at any size */
  const GLYPHS: number[][][] = [
    [[0, 0, 0, 1], [0, 1, 1, 1]],                          // L
    [[0, 0, 1, 0], [0.5, 0, 0.5, 1]],                      // T
    [[0, 0, 0, 1], [1, 0, 1, 1], [0, 0.5, 1, 0.5]],        // H
    [[0, 0, 1, 0], [0, 0, 0, 0.5], [0, 0.5, 1, 0.5], [1, 0.5, 1, 1], [0, 1, 1, 1]], // S
    [[0, 1, 0.5, 0], [0.5, 0, 1, 1], [0.25, 0.5, 0.75, 0.5]], // A
    [[0, 1, 0, 0], [0, 0, 1, 0], [1, 0, 1, 0.55], [0, 0.55, 1, 0.55]], // P
    [[0, 0, 0, 1], [0, 0, 1, 0], [0, 1, 1, 1]],            // C
    [[0, 0, 1, 1], [1, 0, 0, 1]],                          // X
    [[0, 0, 0, 1], [0, 0, 1, 0.5], [0, 0.5, 1, 1]],        // K
  ];

  for (let cell = 0; cell < 4; cell++) {
    const ox = (cell % 2) * S, oy = ((cell / 2) | 0) * S;
    const hue = SIGN_HUES[cell];
    // --- the CASE. Steel, painted, bolted: what the sign looks like at noon.
    a.fillStyle = "#3a3e47"; a.fillRect(ox, oy, S, S);
    hg.fillStyle = "#b4b4b4"; hg.fillRect(ox, oy, S, S);      // case proud
    rg.fillStyle = "#c4c4c4"; rg.fillRect(ox * RS, oy * RS, S * RS, S * RS);
    const FR = 11;                                            // frame width
    a.fillStyle = "#14151a"; a.fillRect(ox + FR, oy + FR, S - FR * 2, S - FR * 2);
    hg.fillStyle = "#6e6e6e"; hg.fillRect(ox + FR, oy + FR, S - FR * 2, S - FR * 2);
    rg.fillStyle = "#8a8a8a";
    rg.fillRect((ox + FR) * RS, (oy + FR) * RS, (S - FR * 2) * RS, (S - FR * 2) * RS);
    // frame highlight along the top edge + shadow along the bottom: the whole
    // reason a black rectangle reads as a recessed face rather than a hole
    a.fillStyle = "rgba(255,255,255,0.14)"; a.fillRect(ox + FR, oy + FR, S - FR * 2, 3);
    a.fillStyle = "rgba(0,0,0,0.45)"; a.fillRect(ox + FR, oy + S - FR - 4, S - FR * 2, 4);
    for (const bx of [ox + FR * 0.5, ox + S - FR * 0.5]) {
      for (const by of [oy + FR * 0.5, oy + S - FR * 0.5]) {
        a.beginPath(); a.arc(bx, by, 3.4, 0, Math.PI * 2);
        a.fillStyle = "#8b909a"; a.fill();
        hg.beginPath(); hg.arc(bx, by, 3.4, 0, Math.PI * 2);
        hg.fillStyle = "#ffffff"; hg.fill();
      }
    }

    // --- the TUBE PATH, collected once and replayed onto all four layers.
    const strokes: ((c: CanvasRenderingContext2D, k: number) => void)[] = [];
    const seg = (x0: number, y0: number, x1: number, y1: number): void => {
      strokes.push((c, k) => {
        c.lineWidth = 5 * k; c.lineCap = "round"; c.lineJoin = "round";
        c.beginPath(); c.moveTo(x0, y0); c.lineTo(x1, y1); c.stroke();
      });
    };
    // the border tube — follows the case, which is what makes the sign a SIGN
    // and not a lit panel. Inset far enough that the frame reads outside it.
    const IN = 26, R = 18;
    strokes.push((c, k) => {
      c.lineWidth = 6 * k; c.lineCap = "round"; c.lineJoin = "round";
      c.beginPath();
      const x = ox + IN, y = oy + IN, ww = S - IN * 2, hh = S - IN * 2;
      c.moveTo(x + R, y);
      c.arcTo(x + ww, y, x + ww, y + hh, R); c.arcTo(x + ww, y + hh, x, y + hh, R);
      c.arcTo(x, y + hh, x, y, R); c.arcTo(x, y, x + ww, y, R);
      c.closePath(); c.stroke();
    });
    // three glyphs across the middle third
    const gy = oy + S * 0.34, gh = S * 0.32, gw = S * 0.17, gap = S * 0.055;
    const gx0 = ox + (S - (gw * 3 + gap * 2)) / 2;
    for (let i = 0; i < 3; i++) {
      const gl = GLYPHS[(cell * 5 + i * 3 + ((Math.random() * 9) | 0)) % GLYPHS.length];
      const bx = gx0 + i * (gw + gap);
      for (const s of gl) seg(bx + s[0] * gw, gy + s[1] * gh, bx + s[2] * gw, gy + s[3] * gh);
    }

    // EMISSIVE: a wide dim halo, then the coloured tube, then a white-hot core.
    // Three passes rather than a canvas blur — `filter` is not free on every
    // browser and this is generated on the main thread at map load.
    for (const [k, col] of [[3.4, `${hue}30`], [2.0, `${hue}70`], [1.0, hue], [0.42, "#ffffff"]] as const) {
      g.strokeStyle = col as string;
      for (const s of strokes) s(g, k as number);
    }
    // ALBEDO: unlit tube glass is a pale tinted rod with a specular line down it
    a.strokeStyle = "rgba(232,238,246,0.30)"; for (const s of strokes) s(a, 1.25);
    a.strokeStyle = `${hue}66`; for (const s of strokes) s(a, 0.9);
    a.strokeStyle = "rgba(255,255,255,0.5)"; for (const s of strokes) s(a, 0.28);
    // HEIGHT: the tube stands off the face — this is what makes it round
    hg.strokeStyle = "#9a9a9a"; for (const s of strokes) s(hg, 1.35);
    hg.strokeStyle = "#ffffff"; for (const s of strokes) s(hg, 0.8);
    // ROUGHNESS: glass is the glossiest thing on the whole sheet
    rg.save(); rg.scale(RS, RS);
    rg.strokeStyle = "#1a1a1a"; for (const s of strokes) s(rg, 1.15);
    rg.restore();
  }
  // grime last, over everything: a sign that has hung outside for ten years
  noise(a, 9000, 0.07);
  return { layers: { albedo, height: hc, rough: rc, bump: 2.6 }, glow };
}

/** Interior lighting colourways, one per shop-window atlas cell: a warm diner,
 *  a cold-white electronics counter, a green grocer and a magenta bar. Four
 *  different rooms is the whole point — a street where every window shows the
 *  same shop is worse than a street of dark windows. */
const SHOP_HUES = ["#ffb45a", "#cfe8ff", "#8dffb0", "#ff5ac8"];

/**
 * SHOP INTERIORS (2026-08-05, `graphics`) — a 2x2 ATLAS, the 22nd family, and
 * the FIRST surface in this game that shows something BEHIND itself.
 *
 * Every window in the game up to now has been paint: the `facade` tile draws a
 * grid of lit office panes flat on a wall, at the wall's own depth. This one is
 * a room — a floor, a ceiling, side walls converging on a back wall, shelves
 * with goods on them, pendant lamps, a counter in the foreground — with a sheet
 * of dirty glass drawn OVER it. The perspective is one-point and fixed, which
 * is wrong from every angle but the front, and at the 1.5-3 m of glazing this
 * is read across that is invisible; what the eye actually gets is depth where
 * there was none.
 *
 * Layered in the order light reaches the eye, which is also the order that
 * makes the glass read as glass:
 *  1. the room (albedo dark, glow layer carries every light in it)
 *  2. the merchandise and the lamps
 *  3. the GLASS — reflection streaks, a sky gradient down the top, and the
 *     grime that collects at the bottom corners of every real shop window.
 * The reflections are in the ALBEDO and the ROUGHNESS but NOT in the emissive,
 * because a reflection is not a light source; the lamps are in all three.
 *
 * The caller maps ONE cell continuously across every pane of a shopfront (see
 * `shopfront`'s `u0`/`u1`), so the room runs behind the mullions instead of
 * repeating in each pane.
 */
function makeShopwin(): { layers: Layers; glow: HTMLCanvasElement } {
  const { c: albedo, g: a } = cvs(ALBEDO);
  const { c: hc, g: hg } = cvs(ALBEDO);
  const { c: rc, g: rg } = cvs(ROUGH);
  const { c: glow, g } = cvs(ALBEDO);
  const S = ALBEDO / 2;
  const RS = ROUGH / ALBEDO;

  g.fillStyle = "#000"; g.fillRect(0, 0, ALBEDO, ALBEDO);
  // glass is the glossiest surface in the map texture set — the roughness map
  // starts BLACK everywhere and only the grime lifts it
  rg.fillStyle = "#1d1d1d"; rg.fillRect(0, 0, ROUGH, ROUGH);
  // ...and it is FLAT: mid-grey height everywhere, so the normal map is quiet
  // and only the grime and the frame edge push it about
  hg.fillStyle = "#808080"; hg.fillRect(0, 0, ALBEDO, ALBEDO);

  for (let cell = 0; cell < 4; cell++) {
    const ox = (cell % 2) * S, oy = ((cell / 2) | 0) * S;
    const hue = SHOP_HUES[cell];
    // ---- 1. THE ROOM. One-point perspective: four trapezoids from the cell
    // edge to an inset back wall. The vanishing rect sits ABOVE centre so the
    // floor gets more of the picture than the ceiling, which is what you see
    // standing on a pavement looking down into a shop.
    const bx = ox + S * 0.17, by = oy + S * 0.13, bw = S * 0.66, bh = S * 0.56;
    const tri = (pts: number[][], fill: string): void => {
      a.beginPath(); a.moveTo(pts[0][0], pts[0][1]);
      for (let i = 1; i < pts.length; i++) a.lineTo(pts[i][0], pts[i][1]);
      a.closePath(); a.fillStyle = fill; a.fill();
    };
    const L = ox, R = ox + S, T = oy, B = oy + S;
    // Values are LIGHTER than a real unlit room, on purpose: this is read
    // through a pane, at a metre or two, against a sunlit stone wall, and the
    // first capture of this pass proved that a physically-honest dark interior
    // is a black rectangle at every distance the game is actually played at.
    tri([[L, T], [R, T], [bx + bw, by], [bx, by]], "#171a22");                  // ceiling
    tri([[L, B], [R, B], [bx + bw, by + bh], [bx, by + bh]], "#332e28");        // floor
    tri([[L, T], [L, B], [bx, by + bh], [bx, by]], "#232733");                  // left wall
    tri([[R, T], [R, B], [bx + bw, by + bh], [bx + bw, by]], "#1b1f28");        // right wall
    a.fillStyle = "#2e3440"; a.fillRect(bx, by, bw, bh);                        // back wall

    // ---- 2. SHELVES + GOODS on the back wall. The shelf strip-light is the
    // brightest line in the room and the goods sit against it — a shop window
    // IS its shelf lighting, and at three bays to a facade this is what the eye
    // resolves first.
    for (let sh = 0; sh < 3; sh++) {
      const sy = by + bh * (0.26 + sh * 0.25);
      a.fillStyle = "#454c5a"; a.fillRect(bx + bw * 0.05, sy, bw * 0.9, 5);
      let gx = bx + bw * 0.07;
      while (gx < bx + bw * 0.92) {
        const gw = bw * (0.08 + Math.random() * 0.12);
        const gh2 = bh * (0.09 + Math.random() * 0.11);
        const lum = 0.55 + Math.random() * 0.45;
        a.fillStyle = `rgba(${(196 * lum) | 0},${(190 * lum) | 0},${(178 * lum) | 0},0.95)`;
        a.fillRect(gx, sy - gh2, gw, gh2);
        a.fillStyle = "rgba(0,0,0,0.35)"; a.fillRect(gx, sy - gh2, 2, gh2);
        // the goods catch the shelf strip-light: a soft bar of hue over each
        g.fillStyle = `${hue}${lum > 0.75 ? "7a" : "44"}`;
        g.fillRect(gx, sy - gh2, gw, gh2);
        gx += gw + bw * 0.025;
      }
      // the strip light UNDER the shelf board
      g.fillStyle = `${hue}d0`; g.fillRect(bx + bw * 0.05, sy + 5, bw * 0.9, 5);
      a.fillStyle = "rgba(255,255,255,0.75)"; a.fillRect(bx + bw * 0.05, sy + 5, bw * 0.9, 4);
    }

    // ---- 3. PENDANT LAMPS. Three hanging discs with a real falloff halo,
    // built the same three-pass way `signage` builds its tubes (canvas `filter`
    // is not free on every browser and this runs on the main thread at load).
    for (let i = 0; i < 3; i++) {
      const lx = ox + S * (0.24 + i * 0.26), ly = oy + S * (0.16 + (i % 2) * 0.05);
      a.strokeStyle = "#3a3f48"; a.lineWidth = 1.5;
      a.beginPath(); a.moveTo(lx, oy); a.lineTo(lx, ly); a.stroke();
      for (const [r, alpha] of [[S * 0.2, "26"], [S * 0.1, "5c"], [S * 0.042, "ff"]] as const) {
        g.beginPath(); g.arc(lx, ly, r as number, 0, Math.PI * 2);
        g.fillStyle = `${hue}${alpha}`; g.fill();
      }
      g.beginPath(); g.arc(lx, ly, S * 0.022, 0, Math.PI * 2);
      g.fillStyle = "#ffffff"; g.fill();
      a.beginPath(); a.arc(lx, ly, S * 0.044, 0, Math.PI * 2);
      a.fillStyle = "#f2f5fa"; a.fill();
    }
    // spill on the floor under the lamps — without it the room's light has no
    // source and the lamps read as stickers
    const spill = g.createLinearGradient(0, by + bh, 0, B);
    spill.addColorStop(0, `${hue}3c`); spill.addColorStop(1, `${hue}00`);
    g.fillStyle = spill; g.fillRect(L, by + bh, S, S - bh - (by - T));

    // ---- 4. THE COUNTER, foreground. A dark slab across the lower third: the
    // occluder that tells the eye the room has a front as well as a back.
    const cy = oy + S * 0.76;
    a.fillStyle = "#14171e"; a.fillRect(ox + S * 0.06, cy, S * 0.76, S * 0.2);
    a.fillStyle = "#3b4150"; a.fillRect(ox + S * 0.06, cy, S * 0.76, 6);
    g.fillStyle = `${hue}30`; g.fillRect(ox + S * 0.06, cy, S * 0.76, 6);

    // ---- 5. THE GLASS. Everything below is ON the pane, not in the room.
    // A sky gradient down the top third: glass at this angle reflects the sky
    // before it shows you anything, and it is the single cue that says "there
    // is a surface here" on a shop you are walking past. Kept WEAK — the first
    // capture had it at 0.30 and the reflection ate the room behind it.
    const sky = a.createLinearGradient(0, T, 0, T + S * 0.4);
    sky.addColorStop(0, "rgba(150,175,205,0.16)");
    sky.addColorStop(1, "rgba(150,175,205,0)");
    a.fillStyle = sky; a.fillRect(L, T, S, S * 0.4);
    // two broad diagonal reflection streaks
    a.save();
    a.beginPath(); a.rect(L, T, S, S); a.clip();
    a.translate(ox + S / 2, oy + S / 2); a.rotate(-0.42);
    for (const [w2, off, al] of [[S * 0.14, -S * 0.22, 0.09], [S * 0.055, S * 0.10, 0.06]] as const) {
      const gr = a.createLinearGradient(off - w2, 0, off + w2, 0);
      gr.addColorStop(0, "rgba(255,255,255,0)");
      gr.addColorStop(0.5, `rgba(232,240,255,${al})`);
      gr.addColorStop(1, "rgba(255,255,255,0)");
      a.fillStyle = gr; a.fillRect(off - w2, -S, w2 * 2, S * 2);
    }
    a.restore();
    // the streaks are the one thing on this pane that is NOT perfectly smooth
    rg.save(); rg.beginPath(); rg.rect(ox * RS, oy * RS, S * RS, S * RS); rg.clip();
    rg.translate((ox + S / 2) * RS, (oy + S / 2) * RS); rg.rotate(-0.42);
    rg.fillStyle = "#3a3a3a"; rg.fillRect(-S * 0.36 * RS, -S * RS, S * 0.28 * RS, S * 2 * RS);
    rg.restore();
    // GRIME, pooled into the bottom corners where rain runs off a pane
    for (let i = 0; i < 90; i++) {
      const t = Math.random();
      const gx2 = ox + (t < 0.5 ? t : 1 - t * 0.35) * S;
      const gy2 = oy + S * (0.55 + Math.random() * 0.45);
      const r = S * (0.02 + Math.random() * 0.08);
      const al = 0.03 + Math.random() * 0.07;
      a.fillStyle = `rgba(${170 + Math.random() * 40 | 0},${168 + Math.random() * 36 | 0},${158 + Math.random() * 32 | 0},${al})`;
      a.beginPath(); a.ellipse(gx2, gy2, r * 1.5, r, 0, 0, 7); a.fill();
      hg.fillStyle = `rgba(255,255,255,${al * 2})`;
      hg.beginPath(); hg.ellipse(gx2, gy2, r * 1.5, r, 0, 0, 7); hg.fill();
      rg.fillStyle = `rgba(255,255,255,${al * 4})`;
      rg.beginPath(); rg.ellipse(gx2 * RS, gy2 * RS, r * 1.5 * RS, r * RS, 0, 0, 7); rg.fill();
    }
  }
  noise(a, 5000, 0.05);
  return { layers: { albedo, height: hc, rough: rc, bump: 0.9 }, glow };
}

/**
 * ROAD PAINT (2026-08-01, `graphics`) — the one texture in this file with a
 * real ALPHA channel.
 *
 * Every other family here is an opaque near-white tile that a material tints.
 * Lane paint cannot work that way: it is a stripe laid ON another surface, and
 * what sells it is the EROSION — thermoplastic chips off at the edges, gets
 * polished bald under a wheel track, and picks up tyre grime. A clean white
 * quad reads as a decal sticker; a chipped one reads as paint.
 *
 * Layout is deliberate and the caller depends on it:
 *   - U runs ALONG the stripe and TILES (`RepeatWrapping`), so a 2.6 m dash and
 *     a 40 m edge line are the same texture at the same world scale.
 *   - V runs ACROSS the stripe and is CLAMPED, with the outer ~8% of the band
 *     feathered into ragged transparency. That is what gives every painted edge
 *     in the game an irregular outline for free.
 *
 * Drawn with alphaTest rather than blending: no sort, no transparent pass, and
 * it still writes depth (see decalTextures.ts for the same argument made the
 * other way round for multiply-blend decals).
 */
let paintCache: { map: THREE.CanvasTexture; normal: THREE.CanvasTexture | null; roughness: THREE.CanvasTexture | null } | null = null;
export function roadPaintTexture(): NonNullable<typeof paintCache> {
  if (paintCache) return paintCache;
  const t0 = performance.now();
  const { c, g } = cvs(ALBEDO);
  const EDGE = ALBEDO * 0.085;      // feathered band at each side
  // the paint body — never pure white; road marking is a warm off-white that
  // has been rained on, and pure #fff blows out under this game's bloom
  g.fillStyle = "#ece8dc";
  g.fillRect(0, EDGE, ALBEDO, ALBEDO - EDGE * 2);
  // TYRE POLISH: broad along-stripe bands where the paint has been burnished
  // thin. Lighter in the middle of a lane, grubby at the sides.
  for (let i = 0; i < 14; i++) {
    const y = EDGE + Math.random() * (ALBEDO - EDGE * 2);
    const hgt = 6 + Math.random() * 26;
    g.fillStyle = `rgba(${150 + Math.random() * 50 | 0},${148 + Math.random() * 48 | 0},${140 + Math.random() * 44 | 0},${0.05 + Math.random() * 0.13})`;
    g.fillRect(0, y, ALBEDO, hgt);
  }
  // grime picked up from the road, pooled toward the stripe's edges
  for (let i = 0; i < 260; i++) {
    const x = Math.random() * ALBEDO;
    const t = Math.random();
    const y = EDGE + (t < 0.5 ? t : 1 - (1 - t)) * (ALBEDO - EDGE * 2);
    const r = 3 + Math.random() * 16;
    g.fillStyle = `rgba(${86 + Math.random() * 60 | 0},${84 + Math.random() * 56 | 0},${78 + Math.random() * 50 | 0},${0.04 + Math.random() * 0.1})`;
    g.beginPath(); g.ellipse(x, y, r * (1.4 + Math.random()), r * 0.6, 0, 0, 7); g.fill();
  }
  noise(g, 46000, 0.1);
  // ---- EROSION. Everything below removes alpha; this is the whole point.
  g.globalCompositeOperation = "destination-out";
  // 1. ragged edges — notches bitten out of both feathered bands
  for (const side of [0, 1]) {
    for (let x = -8; x < ALBEDO + 8; x += 3 + Math.random() * 7) {
      const bite = EDGE * (0.25 + Math.random() * 1.25);
      const y = side ? ALBEDO - EDGE - bite * 0.15 : EDGE - bite * 0.85;
      g.fillStyle = `rgba(0,0,0,${0.45 + Math.random() * 0.55})`;
      g.beginPath();
      g.ellipse(x, side ? y + bite : y, 3 + Math.random() * 9, bite, 0, 0, 7);
      g.fill();
    }
  }
  // 2. chips and scuffs across the body — a few big bald patches, many small
  for (let i = 0; i < 140; i++) {
    const big = Math.random() < 0.12;
    const x = Math.random() * ALBEDO, y = EDGE + Math.random() * (ALBEDO - EDGE * 2);
    const r = big ? 12 + Math.random() * 34 : 1.2 + Math.random() * 6;
    g.fillStyle = `rgba(0,0,0,${big ? 0.35 + Math.random() * 0.5 : 0.5 + Math.random() * 0.5})`;
    g.beginPath();
    for (let k = 0; k < 7; k++) {
      const a = (k / 7) * Math.PI * 2;
      const rr = r * (0.55 + Math.random() * 0.8);
      g.lineTo(x + Math.cos(a) * rr * 1.5, y + Math.sin(a) * rr);
    }
    g.closePath(); g.fill();
  }
  // 3. cracks straight through the film
  g.lineCap = "round";
  for (let i = 0; i < 16; i++) {
    let x = Math.random() * ALBEDO, y = EDGE + Math.random() * (ALBEDO - EDGE * 2);
    g.strokeStyle = `rgba(0,0,0,${0.4 + Math.random() * 0.5})`;
    g.lineWidth = 0.9 + Math.random() * 2.4;
    g.beginPath(); g.moveTo(x, y);
    for (let s = 0; s < 5; s++) { x += (Math.random() - 0.4) * 70; y += (Math.random() - 0.5) * 26; g.lineTo(x, y); }
    g.stroke();
  }
  g.globalCompositeOperation = "source-over";

  const cheap = cheapTextures();
  // HEIGHT: composite the finished paint over black, so "where there is film"
  // is exactly "where it is white". Paint stands ~2 mm proud of asphalt and the
  // bevel at every chipped edge is what stops it looking printed on.
  let normal: THREE.CanvasTexture | null = null;
  let roughness: THREE.CanvasTexture | null = null;
  if (!cheap) {
    const { c: hc, g: hg } = cvs(ALBEDO);
    hg.fillStyle = "#000000"; hg.fillRect(0, 0, ALBEDO, ALBEDO);
    hg.drawImage(c, 0, 0);
    normal = new THREE.CanvasTexture(normalMapFrom(hc, 2.2));
    normal.colorSpace = THREE.NoColorSpace;
    // intact film is SMOOTH (it is a polymer); the exposed aggregate in a chip
    // is as rough as the road around it
    roughness = new THREE.CanvasTexture(roughFrom(hc, 0.34, 0.95));
    roughness.colorSpace = THREE.NoColorSpace;
  }
  const map = new THREE.CanvasTexture(c);
  map.colorSpace = THREE.SRGBColorSpace;
  map.anisotropy = 16; // always seen at a grazing angle — same case as `paving`
  for (const t of [map, normal, roughness]) {
    if (!t) continue;
    t.wrapS = THREE.RepeatWrapping;   // along the stripe
    t.wrapT = THREE.ClampToEdgeWrapping; // across it — the feather must not tile
    t.userData.shared = true;         // module-cached; teardown must not dispose
    if (t !== map) t.anisotropy = 8;
  }
  paintCache = { map, normal, roughness };
  console.log(`[maptex] generated road paint in ${(performance.now() - t0).toFixed(0)} ms`);
  return paintCache;
}

/* ---------------------------------------------------------------------------
 * GRIME — the floor-contact atlas (2026-08-05, mission `graphics`).
 *
 * A MULTIPLY texture, so white is "leave this pixel alone" and everything drawn
 * is a darkening. That is the whole reason this family looks nothing like the
 * others in this file: it carries no colour of its own, it only removes light,
 * which is what lets one 512² sheet ground a building on grass, on asphalt and
 * on a rooftop deck without ever being told which.
 *
 * 2 x 2 atlas of 256² cells (the caller indexes them 0..3):
 *   0 — SKIRT ramp. Dark along v = 1 (the wall edge), out to white by v ≈ 0.45.
 *       The base ramp is a function of v ALONE so two adjacent segments of one
 *       wall can never show a seam; all the irregularity on top of it is masked
 *       to zero at u = 0 and u = 1 for exactly the same reason.
 *   1..3 — dirt BLOTS: a worn patch, a spill/stain, and a gravel-and-grit
 *       scatter. Each has a hard white margin so its cell edge is invisible and
 *       so mip bleed between neighbouring cells stays harmless.
 *
 * No normal or roughness map, deliberately: this is dirt ON a surface, and the
 * surface underneath already supplies both. One 512² sRGB upload, module cached.
 * ------------------------------------------------------------------------- */
let grimeCache: THREE.CanvasTexture | null = null;
export function groundGrimeTexture(): THREE.CanvasTexture {
  if (grimeCache) return grimeCache;
  const t0 = performance.now();
  const S = 256;                        // one cell
  const { c, g } = cvs(S * 2);
  g.fillStyle = "#ffffff"; g.fillRect(0, 0, S * 2, S * 2);
  // deterministic enough to reason about, random enough to look like dirt
  const R = (): number => Math.random();

  // ---- CELL 0 (canvas top-left): the wall-base ramp.
  {
    const ramp = g.createLinearGradient(0, 0, 0, S * 0.7);
    // The first cut stopped at 0.58 of the cell with a 0.52 mid-stop and the
    // capture could barely find it on a 6 m wall. An ambient contact term is
    // strong AND long — most of the sky over the metre of floor beside a wall
    // is blocked by that wall — so the ramp now runs 70% of the cell and holds
    // its darkness through the first third instead of falling off immediately.
    ramp.addColorStop(0, "rgba(38,36,44,0.86)");
    ramp.addColorStop(0.18, "rgba(56,54,62,0.66)");
    ramp.addColorStop(0.45, "rgba(92,90,98,0.36)");
    ramp.addColorStop(0.75, "rgba(140,138,146,0.14)");
    ramp.addColorStop(1, "rgba(255,255,255,0)");
    g.fillStyle = ramp; g.fillRect(0, 0, S, S * 0.7);
    // TONGUES — the ragged reach of the shadow, faded to nothing at both u
    // edges so segment joins stay invisible (see the header).
    g.save();
    g.beginPath(); g.rect(0, 0, S, S); g.clip();
    for (let i = 0; i < 26; i++) {
      const x = R() * S;
      const edgeFade = Math.min(1, Math.min(x, S - x) / 34);
      if (edgeFade <= 0) continue;
      const rw = 8 + R() * 34, rh = S * (0.16 + R() * 0.3);
      const grd = g.createRadialGradient(x, 0, 0, x, 0, rh);
      grd.addColorStop(0, `rgba(64,62,70,${(0.1 + R() * 0.2) * edgeFade})`);
      grd.addColorStop(1, "rgba(255,255,255,0)");
      g.fillStyle = grd;
      g.beginPath(); g.ellipse(x, 0, rw, rh, 0, 0, 7); g.fill();
    }
    // grit and splash gathered in the crease
    for (let i = 0; i < 220; i++) {
      const x = R() * S, y = Math.pow(R(), 2.2) * S * 0.5;
      const edgeFade = Math.min(1, Math.min(x, S - x) / 20);
      g.fillStyle = `rgba(${60 + R() * 50 | 0},${58 + R() * 48 | 0},${62 + R() * 46 | 0},${(0.05 + R() * 0.22) * edgeFade})`;
      g.beginPath(); g.ellipse(x, y, 0.8 + R() * 3.4, 0.6 + R() * 2.2, 0, 0, 7); g.fill();
    }
    g.restore();
  }

  // ---- CELLS 1..3: the blots. `ox,oy` is the cell's top-left in canvas space.
  //
  // Each is drawn on its OWN transparent canvas and then masked with
  // `destination-in` before it is composited into the atlas. The first cut
  // clipped to the cell rect instead, and the capture showed exactly what that
  // predicts: a patch whose gradient was still strong when it reached the cell
  // boundary got sliced there, so half the dirt on the floor read as dark
  // SQUARES. A radial mask that reaches zero at the inscribed circle cannot do
  // that — and it also guarantees the pure-white margin the atlas needs.
  const atlas = g;
  const blot = (ox: number, oy: number, kind: 0 | 1 | 2): void => {
    const { c: bc, g: bg } = cvs(S);
    const g = bg;                        // shadows the atlas ctx for the drawing below
    const cxx = S / 2, cyy = S / 2;
    if (kind === 0) {
      // WORN PATCH: broad, soft, barely there — the traffic polish that stops a
      // 60 m plaza reading as one tile repeated 400 times.
      for (let i = 0; i < 9; i++) {
        const x = cxx + (R() - 0.5) * S * 0.42, y = cyy + (R() - 0.5) * S * 0.42;
        const r = S * (0.16 + R() * 0.22);
        const grd = g.createRadialGradient(x, y, 0, x, y, r);
        grd.addColorStop(0, `rgba(118,116,112,${0.17 + R() * 0.2})`);
        grd.addColorStop(0.6, `rgba(146,144,140,${0.09 + R() * 0.1})`);
        grd.addColorStop(1, "rgba(255,255,255,0)");
        g.fillStyle = grd; g.beginPath(); g.ellipse(x, y, r * (0.8 + R() * 0.5), r, R() * 3, 0, 7); g.fill();
      }
    } else if (kind === 1) {
      // STAIN: one dark irregular pool with a defined edge and a lighter halo —
      // oil under where something was parked, or standing water that dried.
      const r = S * 0.3;
      const grd = g.createRadialGradient(cxx, cyy, 0, cxx, cyy, r * 1.5);
      grd.addColorStop(0, "rgba(52,50,54,0.62)");
      grd.addColorStop(0.5, "rgba(86,84,88,0.34)");
      grd.addColorStop(1, "rgba(255,255,255,0)");
      g.fillStyle = grd;
      g.beginPath();
      for (let k = 0; k <= 22; k++) {
        const a = (k / 22) * Math.PI * 2;
        const rr = r * (0.62 + Math.sin(a * 3.1) * 0.14 + Math.sin(a * 5.7) * 0.1 + R() * 0.16);
        g.lineTo(cxx + Math.cos(a) * rr * 1.25, cyy + Math.sin(a) * rr);
      }
      g.closePath(); g.fill();
      // run-off fingers
      for (let i = 0; i < 7; i++) {
        const a = R() * Math.PI * 2, len = r * (0.5 + R());
        g.strokeStyle = `rgba(76,74,78,${0.1 + R() * 0.18})`;
        g.lineWidth = 2 + R() * 7; g.lineCap = "round";
        g.beginPath(); g.moveTo(cxx + Math.cos(a) * r * 0.5, cyy + Math.sin(a) * r * 0.5);
        g.lineTo(cxx + Math.cos(a) * len, cyy + Math.sin(a) * len); g.stroke();
      }
    } else {
      // GRIT: swept debris — hundreds of specks with a soft shadow under the
      // drift. The one blot that survives being stood on top of.
      const grd = g.createRadialGradient(cxx, cyy, 0, cxx, cyy, S * 0.42);
      grd.addColorStop(0, "rgba(126,122,116,0.32)");
      grd.addColorStop(1, "rgba(255,255,255,0)");
      g.fillStyle = grd; g.fillRect(0, 0, S, S);
      for (let i = 0; i < 420; i++) {
        const a = R() * Math.PI * 2, rad = Math.pow(R(), 0.6) * S * 0.42;
        const x = cxx + Math.cos(a) * rad * 1.2, y = cyy + Math.sin(a) * rad;
        const fade = 1 - rad / (S * 0.42);
        g.fillStyle = `rgba(${54 + R() * 60 | 0},${52 + R() * 58 | 0},${50 + R() * 56 | 0},${(0.12 + R() * 0.45) * fade})`;
        g.beginPath(); g.ellipse(x, y, 0.7 + R() * 2.6, 0.6 + R() * 2, R() * 3, 0, 7); g.fill();
      }
    }
    // the mask: opaque out to 0.62 of the half-width, gone by the inscribed
    // circle, so no cell edge and no corner can carry any ink at all
    g.globalCompositeOperation = "destination-in";
    const mask = g.createRadialGradient(cxx, cyy, 0, cxx, cyy, S / 2);
    mask.addColorStop(0, "rgba(0,0,0,1)");
    mask.addColorStop(0.62, "rgba(0,0,0,1)");
    mask.addColorStop(0.9, "rgba(0,0,0,0.35)");
    mask.addColorStop(1, "rgba(0,0,0,0)");
    g.fillStyle = mask; g.fillRect(0, 0, S, S);
    g.globalCompositeOperation = "source-over";
    atlas.drawImage(bc, ox, oy);
  };
  blot(S, 0, 0);
  blot(0, S, 1);
  blot(S, S, 2);

  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  // clamped on BOTH axes: every cell is addressed by an explicit uv rect and
  // nothing here tiles — a wrap would wrap the neighbouring cell in.
  t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
  t.anisotropy = 16;                  // always read at a grazing angle: it is the floor
  t.userData.shared = true;           // module-cached; teardown must not dispose
  grimeCache = t;
  console.log(`[maptex] generated ground grime in ${(performance.now() - t0).toFixed(0)} ms`);
  return t;
}

/** renderer.ts owns `lowSpec()`, but importing it here would close an ESM
 *  cycle (renderer -> mapTextures -> renderer) around module-init order that
 *  this file has no reason to risk. The flag is two localStorage keys; read
 *  them directly and keep the dependency one-way. */
export function cheapTextures(): boolean {
  try {
    if (localStorage.getItem("neonpurge.tvapp") === "1") return true;
    if (localStorage.getItem("neonpurge.highgfx") === "1") return false;
    return localStorage.getItem("neonpurge.lowspec") === "1";
  } catch { return false; }
}

/** Incremental builder state. The 26 families used to be one synchronous
 *  `cache = {...}` literal — ~5.9 s under swiftshader, and it sat on the BLOCKING
 *  boot path (`preloadGameAssets`) even though NO menu screen renders a map
 *  texture (the menu background is avatar-only). Splitting it into one step per
 *  family lets `warmOneMapFamily()` pump the whole set on idle AFTER the menu is
 *  interactive — the same proven treatment the 2026-08-26 pass gave the monster
 *  and prop families — so the boot overlay clears ~5.8 s sooner. `mapTextures()`
 *  stays the force-all backstop: the map picker (previews.ts) and the match load
 *  screen (renderer.precompile) both call it, so a family is ALWAYS complete
 *  before anything reads it, regardless of how far the idle pump has got. */
let warm: {
  steps: Array<() => void>;
  i: number;
  partial: Partial<MapTexCache>;
  t0: number;
  cheap: boolean;
} | null = null;

function beginMapWarm(): NonNullable<typeof warm> {
  if (warm) return warm;
  const cheap = cheapTextures(); // weak machines skip normal + roughness entirely
  const mk = (canvas: HTMLCanvasElement, aniso: number, srgb: boolean): THREE.CanvasTexture => {
    const t = new THREE.CanvasTexture(canvas);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.anisotropy = aniso;
    t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
    return t;
  };
  const build = (l: Layers, aniso = 8, roughLo = 0.35, roughHi = 0.95): FamilyTex => ({
    map: mk(l.albedo, aniso, true),
    normal: cheap || l.bump <= 0 ? null : mk(normalMapFrom(l.height ?? l.albedo, l.bump), aniso, false),
    roughness: cheap ? null : mk(l.rough ?? roughFrom(l.albedo, roughLo, roughHi), 1, false),
    bump: l.bump,
  });
  const p: Partial<MapTexCache> = {};
  // fac/sgn/shp are shared between a family and its glow atlas — assigned in the
  // family step, consumed in the final glow step (which always runs LAST).
  let fac!: ReturnType<typeof makeFacade>;
  let sgn!: ReturnType<typeof makeSignage>;
  let shp!: ReturnType<typeof makeShopwin>;
  const steps: Array<() => void> = [
    () => { p.surface = build(makeSurface(), 4, 0.22, 0.8); },
    // PLASTER — painted rendered stucco, the default for generic structural
    // walls that used to wear the metal `surface`. Matte, seen head-on over a
    // large area, so it takes `surface`'s low anisotropy; the roughness band
    // sits high and narrow (a painted wall is uniformly matte — the interest is
    // the trowel-sweep normal, not a specular hotspot).
    () => { p.plaster = build(makePlaster(), 4, 0.6, 0.98); },
    // WOOD — sawn plank cladding for the timber maps (ship hull, western town).
    // Seen head-on over a wall like `plaster`, so it takes the same low
    // anisotropy; the roughness band is wide because a shadowed board gap is
    // dust-matte where the cupped board crown catches a soft sheen.
    () => { p.wood = build(makeWood(), 4, 0.5, 0.95); },
    () => { p.concrete = build(makeConcrete(), 4, 0.55, 0.98); },
    () => { fac = makeFacade(); p.facade = build(fac.layers, 4, 0.12, 0.85); },
    () => { p.terrain = build(makeTerrain(), 8, 0.6, 1.0); },
    () => { p.trim = build(makeTrim(), 8, 0.45, 0.95); },
    // aniso 16: the floor is the ONE surface always seen at a grazing angle,
    // where anisotropic filtering is the difference between a legible joint
    // grid receding to the horizon and a grey smear. It is a sampler flag on
    // one texture, not a per-frame cost.
    () => { p.paving = build(makePaving(), 16, 0.5, 0.98); },
    // the natural-decor trio. `bark` and `canopy` are seen almost exclusively
    // at a grazing angle on a curved surface, so they get the paving treatment
    // on anisotropy; `boulder` is a small object read head-on.
    () => { p.bark = build(makeBark(), 16, 0.6, 1.0); },
    () => { p.canopy = build(makeCanopy(), 16, 0.5, 1.0); },
    // NEEDLE — conifer foliage, seen at a grazing angle on drooping cones like
    // `canopy`, so it takes the same anisotropy. Roughness band sits high: pine
    // is matte, the read comes from the normal, not a specular hotspot.
    () => { p.needle = build(makeNeedle(), 16, 0.55, 1.0); },
    // HILLSIDE — the distant mountain ring. Seen at a grazing angle over a huge
    // area like `terrain`, so it takes the same anisotropy; roughness sits high
    // (a wooded slope is matte, the read is silhouette + normal, never a
    // specular hotspot).
    () => { p.hillside = build(makeHillside(), 16, 0.6, 1.0); },
    () => { p.boulder = build(makeBoulder(), 8, 0.42, 0.95); },
    // CLOTH is read at a grazing angle on a curved, moving sheet — the same
    // case that earned `paving`/`bark` their anisotropy. Roughness band is
    // deliberately narrow and HIGH: fabric is matte, and the interest has to
    // come from the normal map, not from a specular hotspot.
    () => { p.cloth = build(makeCloth(), 16, 0.72, 1.0); },
    // ROOFING is the floor of every rooftop map and the top of every building
    // on every other one — always seen at a grazing angle over a big area, i.e.
    // exactly the `paving` case, so it gets `paving`'s anisotropy. Bitumen and
    // gravel stay matte across the roof.
    () => { p.roofing = build(makeRoofing(), 16, 0.68, 1.0); },
    // GALV is read head-on on small objects at close range (`boulder`'s case),
    // and it is the one metal in this file — its roughness floor is the lowest
    // of any family so the rib crests can actually catch the sun.
    () => { p.galv = build(makeGalv(), 8, 0.28, 0.78); },
    // LOUVRE is read head-on on a small object, like `galv`, but its roughness
    // band is narrower and higher: a condenser grille is powder-coated and dusty
    // where galvanised sheet is bare zinc, so it must NOT pick up the same sky
    // highlight — the contrast between the two is what separates the plant into
    // two kinds of machine instead of one grey mass.
    () => { p.louvre = build(makeLouvre(), 8, 0.42, 0.9); },
    // SIGNAGE is an ATLAS, so it is the one family whose UVs never tile — the
    // caller pins each quad inside one 0.5x0.5 cell. Anisotropy still matters
    // (a blade sign is read almost edge-on from down the street) and the
    // roughness band is the widest in the file on purpose: bare glass tube next
    // to a painted steel case is the strongest material contrast the map mesh
    // has anywhere.
    () => { sgn = makeSignage(); p.signage = build(sgn.layers, 16, 0.1, 0.95); },
    // SHOPWIN is an atlas like `signage`, and like it is read edge-on down a
    // street, so it takes the same anisotropy. Its roughness band is the LOWEST
    // in the whole file: this is plate glass, and if it picks up the same matte
    // response as the stone next to it the room behind it stops reading as
    // being behind anything.
    () => { shp = makeShopwin(); p.shopwin = build(shp.layers, 16, 0.04, 0.55); },
    () => {
      const minecraft = build(makeMinecraftBlock(), 8, 0.6, 0.95);
      // the voxel grain must stay CRISP — that's the whole point of the family
      minecraft.map.magFilter = THREE.NearestFilter;
      minecraft.map.minFilter = THREE.NearestMipmapNearestFilter;
      p.minecraft = minecraft;
    },
    // every mc family gets the per-1m-cell variation stamp (blockCells) so a
    // merged slab reads as individual blocks, like the source material
    () => { p.mcStone = build((() => { const l = makeStone(); blockCells(l.albedo, 0.09); return l; })(), 8, 0.5, 0.98); },
    () => { p.mcBrick = build((() => { const l = makeMcBrick(); blockCells(l.albedo, 0.08); return l; })(), 8, 0.55, 0.98); },
    () => { p.mcWood = build((() => { const l = makeMcWood(); blockCells(l.albedo, 0.07); return l; })(), 8, 0.4, 0.9); },
    () => { p.mcFoliage = build((() => { const l = makeMcLeaves(); blockCells(l.albedo, 0.14, 0.2); return l; })(), 8, 0.6, 1.0); },
    () => { p.mcEarth = build((() => { const l = makeEarth(); blockCells(l.albedo, 0.12); return l; })(), 8, 0.65, 1.0); },
    () => { p.mcGlass = build(makeGlass(), 8, 0.02, 0.2); },
    // GLOWS last — they reuse the fac/sgn/shp built by the facade/signage/shopwin
    // steps above (all of which precede this one in the ordered list).
    () => {
      p.facadeGlow = mk(fac.glow, 4, true);
      p.signageGlow = mk(sgn.glow, 16, true);
      p.shopwinGlow = mk(shp.glow, 16, true);
    },
  ];
  warm = { steps, i: 0, partial: p, t0: performance.now(), cheap };
  return warm;
}

function finalizeMapWarm(): void {
  const w = warm!;
  cache = w.partial as MapTexCache;
  // module-cached, shared by every map material — scene teardown must never
  // dispose these ("one GPU upload" contract; disposal forced a re-upload
  // stall every round)
  for (const [fam, v] of Object.entries(cache)) {
    if (v instanceof THREE.Texture) { v.userData.shared = true; continue; }
    // npFamily: pbrTextures.upgradeMaterial finds a material's family by its
    // albedo, so the photo set can replace it on ANY call site generically
    v.map.userData.npFamily = fam;
    for (const t of [v.map, v.normal, v.roughness]) if (t) t.userData.shared = true;
  }
  console.log(`[maptex] generated 26 families${w.cheap ? " (albedo only — lowSpec)" : " + normal + roughness"} in ${(performance.now() - w.t0).toFixed(0)} ms`);
  warm = null;
}

/** Build the NEXT not-yet-built map-texture family and return `true` while more
 *  remain (so the idle pump re-queues), `false` once every family is warm.
 *  Idempotent + safe to interleave with `mapTextures()`: if the force-all path
 *  ran first, `cache` is set and this returns `false` immediately. One family
 *  per call keeps any single idle slice to ~one family's cost (~200 ms), like
 *  `warmOneMonsterFamily`. */
export function warmOneMapFamily(): boolean {
  if (cache) return false;
  const w = beginMapWarm();
  if (w.i < w.steps.length) w.steps[w.i++]();
  if (w.i >= w.steps.length) { finalizeMapWarm(); return false; }
  return true;
}

export function mapTextures(): MapTexCache {
  if (cache) return cache;
  // Force-all backstop: complete any families the idle pump hasn't reached yet,
  // synchronously, so callers always see the full set.
  const w = beginMapWarm();
  while (w.i < w.steps.length) w.steps[w.i++]();
  finalizeMapWarm();
  return cache!;
}

/** Fold one family's albedo/normal/roughness into MeshStandardMaterial params.
 *  Every call site used to hand-pick `map` and then forget the detail maps —
 *  this keeps the three in lockstep. `normalScale` is a shared per-family
 *  Vector2 (never allocated per material) so a hundred merged cells still
 *  upload one uniform. */
const normalScales = new Map<string, THREE.Vector2>();
export function surfaceMaps(
  family: SurfaceFamily,
  params: THREE.MeshStandardMaterialParameters,
  scale = 1,
): THREE.MeshStandardMaterialParameters {
  const f = mapTextures()[family];
  params.map = f.map;
  if (f.normal) {
    params.normalMap = f.normal;
    const key = `${family}|${scale}`;
    params.normalScale = normalScales.get(key)
      ?? normalScales.set(key, new THREE.Vector2(scale, scale)).get(key)!;
  }
  if (f.roughness) params.roughnessMap = f.roughness;
  return params;
}

/**
 * Rewrite a box's UVs so the texture tiles at ~`tile` world-units per repeat on
 * every face (accounting for each face's real dimensions) — no stretching.
 */
let gradientCache: THREE.DataTexture | null = null;
/** 6-step gradient map → MeshToonMaterial renders in cel-shaded bands. Widened
 *  from 4 bands in the `graphics` pass: 4 was coarse enough that a rounded
 *  limb still read as one flat facet, which is a big part of why the avatars
 *  looked like stacked cubes no matter what shape they actually were. */
export function toonGradient(): THREE.DataTexture {
  if (gradientCache) return gradientCache;
  const data = new Uint8Array([64, 104, 146, 186, 220, 255]);
  const t = new THREE.DataTexture(data, data.length, 1, THREE.RedFormat);
  t.minFilter = t.magFilter = THREE.LinearFilter;
  t.needsUpdate = true;
  t.userData.shared = true; // module-cached — never disposed by teardown
  gradientCache = t;
  return t;
}

/** Box-projected world-unit UVs for ANY geometry (rounded boxes included):
 *  each vertex maps by its dominant normal axis, so textures tile in world
 *  units without stretching — the RoundedBoxGeometry replacement for
 *  worldTileUV.
 *
 *  `ox/oy/oz` (2026-10-23): the geometry's eventual world translation, for
 *  callers that project BEFORE translating. Without it every box's pattern was
 *  anchored at its own centre, so two abutting floor slabs / wall runs showed a
 *  hard texture seam at the joint, every same-size box repeated the identical
 *  patch, and two overlapping coplanar trim pieces sampled DIFFERENT texels
 *  (a visible z-fight flicker). Pass the translation through `uvAnchor` (it is
 *  wrapped by the tile, so UVs stay small — mediump-safe — and the result is
 *  identical modulo the repeat). */
export function worldProjectUV(
  geo: THREE.BufferGeometry, tile: number, ox = 0, oy = 0, oz = 0,
): void {
  const pos = geo.attributes.position;
  const nor = geo.attributes.normal;
  const uv = geo.attributes.uv;
  for (let i = 0; i < pos.count; i++) {
    const nx = Math.abs(nor.getX(i)), ny = Math.abs(nor.getY(i)), nz = Math.abs(nor.getZ(i));
    let u: number, v: number;
    if (nx >= ny && nx >= nz) { u = pos.getZ(i) + oz; v = pos.getY(i) + oy; }
    else if (ny >= nx && ny >= nz) { u = pos.getX(i) + ox; v = pos.getZ(i) + oz; }
    else { u = pos.getX(i) + ox; v = pos.getY(i) + oy; }
    uv.setXY(i, u / tile, v / tile);
  }
  uv.needsUpdate = true;
}

/** Wrap a world offset into [0, tile) so world-anchored UVs keep small
 *  magnitudes (a 1 km map coordinate / 3 m tile is a UV of ~300, which a
 *  mediump fragment pipeline cannot interpolate cleanly). The texture repeats
 *  every `tile`, so the wrapped offset samples the identical texels. */
export function uvAnchor(c: number, tile: number): number {
  const m = c % tile;
  return m < 0 ? m + tile : m;
}

export function worldTileUV(geo: THREE.BoxGeometry, w: number, h: number, d: number, tile: number): void {
  const uv = geo.attributes.uv;
  // BoxGeometry face order: +x, -x, +y, -y, +z, -z (4 verts each)
  const faceScale: [number, number][] = [
    [d / tile, h / tile], [d / tile, h / tile],
    [w / tile, d / tile], [w / tile, d / tile],
    [w / tile, h / tile], [w / tile, h / tile],
  ];
  for (let f = 0; f < 6; f++) {
    const [su, sv] = faceScale[f];
    for (let i = 0; i < 4; i++) {
      const idx = f * 4 + i;
      uv.setXY(idx, uv.getX(idx) * su, uv.getY(idx) * sv);
    }
  }
  uv.needsUpdate = true;
}
